// ============================================================
// AMERICANO-FAMILY TOURNAMENT ENGINE
// ============================================================
// Pure matchmaking / round-generation / standings logic for the americano
// tournament family: americano, team_americano, mexicano, mixicano,
// team_mexicano, mix_americano, and the club_* variants
// (club_americano, club_team_americano, club_mexicano).
//
// Extracted verbatim from the original monolith (incremental TypeScript split).
// Comments and Norwegian UI/console strings are preserved exactly.
//
// NOTE: the *Engine wiring objects (americanoEngine, mexicanoEngine, ...) and
// their `Padel24Engine.<format> = ...` attachments now live in src/engine
// alongside this module, bound to the Padel24Engine runtime. They
// reference the functions/constants exported from this module.
// ============================================================

export const AMERICANO_PARTNER_COST = 1000;
export const AMERICANO_OPPONENT_COST = 50;
export const AMERICANO_IMBALANCE_COST = 0.01;

// Number of iterations in the search algorithm — more = better quality, but slower
export const AMERICANO_SEARCH_ITERATIONS = 500;

import { DEFAULT_RANKING_AMERICANO } from './constants.js';

// Build a history of partner/opponent pairings from previous matches
// (Second parameter is accepted but unused — one call site in the club_mexicano
//  round generator passes an extra `activePlayers` arg that this function ignores.
//  Kept for behavioural parity with the original single-file implementation.)
export function buildAmericanoHistory(matches, _activePlayers?: any) {
  const partnerCount = new Map(); // 'idA:idB' -> number of times partnered
  const opponentCount = new Map(); // 'idA:idB' -> number of times opponents
  const benchCount = new Map(); // 'playerId' -> number of times benched

  const key = (a, b) => [a, b].sort().join(':');

  for (const m of matches) {
    if (!m.team1 || !m.team2) continue;
    const t1 = [m.team1.p1Id, m.team1.p2Id];
    const t2 = [m.team2.p1Id, m.team2.p2Id];

    partnerCount.set(key(t1[0], t1[1]), (partnerCount.get(key(t1[0], t1[1])) || 0) + 1);
    partnerCount.set(key(t2[0], t2[1]), (partnerCount.get(key(t2[0], t2[1])) || 0) + 1);

    for (const a of t1)
      for (const b of t2) {
        opponentCount.set(key(a, b), (opponentCount.get(key(a, b)) || 0) + 1);
      }

    // Count a benched round for players who are on the "benched" list
    if (Array.isArray(m.benched)) {
      for (const id of m.benched) {
        benchCount.set(id, (benchCount.get(id) || 0) + 1);
      }
    }
  }

  return { partnerCount, opponentCount, benchCount, key };
}

// Calculate the cost of a match setup for one round
// roundPlan: [{ team1: [id1, id2], team2: [id3, id4] }, ...]
export function calcRoundCost(roundPlan, history, players) {
  const { partnerCount, opponentCount, key } = history;
  let cost = 0;

  for (const match of roundPlan) {
    const [a, b] = match.team1;
    const [c, d] = match.team2;

    // Partner cost (squared to strongly avoid repeats)
    const pAB = partnerCount.get(key(a, b)) || 0;
    const pCD = partnerCount.get(key(c, d)) || 0;
    cost += pAB * pAB * AMERICANO_PARTNER_COST;
    cost += pCD * pCD * AMERICANO_PARTNER_COST;

    // Opponent cost (all 4 combinations)
    const oAC = opponentCount.get(key(a, c)) || 0;
    const oAD = opponentCount.get(key(a, d)) || 0;
    const oBC = opponentCount.get(key(b, c)) || 0;
    const oBD = opponentCount.get(key(b, d)) || 0;
    cost += (oAC * oAC + oAD * oAD + oBC * oBC + oBD * oBD) * AMERICANO_OPPONENT_COST;

    // ELO imbalance
    const pa = players.find((p) => p.id === a)?.elo || 1500;
    const pb = players.find((p) => p.id === b)?.elo || 1500;
    const pc = players.find((p) => p.id === c)?.elo || 1500;
    const pd = players.find((p) => p.id === d)?.elo || 1500;
    const t1Avg = (pa + pb) / 2;
    const t2Avg = (pc + pd) / 2;
    cost += Math.abs(t1Avg - t2Avg) * AMERICANO_IMBALANCE_COST;
  }

  return cost;
}

// Shuffle an array (Fisher-Yates)
export function shuffle(arr) {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ──── TENNIS SET VALIDATION ────────────────────────────────────
//
// Used in endMode='first_set' (Team Mexicano default).
// A tennis set is finished when:
//   - 6-0, 6-1, 6-2, 6-3, 6-4   (ordinary set win)
//   - 7-5                        (when 5-5 → 7-5)
//   - 7-6                        (tiebreak at 6-6)
//
// Returns { complete: bool, winner: 1|2|null }
//   complete=true means the set is finished in playing terms.
//   winner=1 if team1 won, 2 if team2.
export function isTennisSetComplete(g1, g2) {
  if (typeof g1 !== 'number' || typeof g2 !== 'number') {
    return { complete: false, winner: null };
  }
  if (g1 < 0 || g2 < 0) return { complete: false, winner: null };

  // A set must reach at least 6 games for one of the teams
  const hi = Math.max(g1, g2);
  const lo = Math.min(g1, g2);

  // 6-0 to 6-4: ordinary win
  if (hi === 6 && lo <= 4) {
    return { complete: true, winner: g1 > g2 ? 1 : 2 };
  }
  // 7-5: won from 5-5
  if (hi === 7 && lo === 5) {
    return { complete: true, winner: g1 > g2 ? 1 : 2 };
  }
  // 7-6: tiebreak at 6-6
  if (hi === 7 && lo === 6) {
    return { complete: true, winner: g1 > g2 ? 1 : 2 };
  }
  return { complete: false, winner: null };
}

// Random round-plan: split active players into quartets, give each quartet a team distribution
export function randomRoundPlan(activePlayerIds) {
  const shuffled = shuffle(activePlayerIds);
  const plan = [];
  for (let i = 0; i + 3 < shuffled.length; i += 4) {
    const four = [shuffled[i], shuffled[i + 1], shuffled[i + 2], shuffled[i + 3]];
    // Pick one of 3 possible team distributions at random
    const variant = Math.floor(Math.random() * 3);
    if (variant === 0) plan.push({ team1: [four[0], four[1]], team2: [four[2], four[3]] });
    else if (variant === 1) plan.push({ team1: [four[0], four[2]], team2: [four[1], four[3]] });
    else plan.push({ team1: [four[0], four[3]], team2: [four[1], four[2]] });
  }
  return plan;
}

// Main algorithm: try many random round-plans, pick the cheapest
export function generateAmericanoRoundPlan(activePlayerIds, history, players) {
  if (activePlayerIds.length < 4 || activePlayerIds.length % 4 !== 0) {
    throw new Error(`Antall aktive spillere må være delelig på 4 (fikk ${activePlayerIds.length})`);
  }

  let bestPlan = null;
  let bestCost = Infinity;

  for (let iter = 0; iter < AMERICANO_SEARCH_ITERATIONS; iter++) {
    const plan = randomRoundPlan(activePlayerIds);
    const cost = calcRoundCost(plan, history, players);
    if (cost < bestCost) {
      bestCost = cost;
      bestPlan = plan;
      // Early termination: if cost = 0, no repetition — perfect
      if (cost === 0) break;
    }
  }

  return bestPlan;
}

// Pick which players should sit out, based on who has been benched the least so far
export function selectBenched(playerIds, benchCount, neededBench) {
  if (neededBench === 0) return [];

  // Sort players: fewest benched first are prioritized to PLAY.
  // Those last in the list (who have been benched the most) get to play.
  // The players with the most benched rounds are left over.
  // We want those who have been benched the LEAST to sit out now.
  const sorted = [...playerIds].sort((a, b) => {
    const ba = benchCount.get(a) || 0;
    const bb = benchCount.get(b) || 0;
    return ba - bb; // fewest benched first
  });

  // Take the first N (those with the fewest benched rounds) — they sit out now
  // to even out who plays the most
  return sorted.slice(0, neededBench);
}

// ============================================================
// TEAM AMERICANO — round-plan over fixed teams
// ============================================================
// Each "team" is a fixed pair { id, p1Id, p2Id }. The cost function considers
// only opponent history and ELO balance — partner history is irrelevant
// since the pairs are never broken up. We generate quartets of 2 teams per match.

// Build team history from played matches. A "team-key" is consistent across
// p1/p2 order by sorting the ids.
export function teamKey(p1Id, p2Id) {
  return [p1Id, p2Id].sort().join(':');
}

export function buildTeamHistory(matches, teams) {
  const teamOpponent = new Map(); // 'tk1::tk2' -> times met
  const teamBenched = new Map(); // 'teamKey' -> number of times benched
  const oppKey = (a, b) => [a, b].sort().join('::');

  for (const m of matches) {
    if (!m.team1 || !m.team2) continue;
    const tk1 = teamKey(m.team1.p1Id, m.team1.p2Id);
    const tk2 = teamKey(m.team2.p1Id, m.team2.p2Id);
    teamOpponent.set(oppKey(tk1, tk2), (teamOpponent.get(oppKey(tk1, tk2)) || 0) + 1);
  }

  // Benched for teams: counted once per round (the benched list is on the match
  // and is identical for all matches in the same round). We only take the first match
  // in each round, and check which player ids were on the benched list
  // — these are linked back to team keys.
  const seenRounds = new Set();
  for (const m of matches) {
    if (seenRounds.has(m.round)) continue;
    seenRounds.add(m.round);
    if (!Array.isArray(m.benched)) continue;
    // A team sits out if BOTH players are in the benched list.
    const benchedSet = new Set(m.benched);
    for (const t of teams) {
      if (benchedSet.has(t.p1Id) && benchedSet.has(t.p2Id)) {
        const tk = teamKey(t.p1Id, t.p2Id);
        teamBenched.set(tk, (teamBenched.get(tk) || 0) + 1);
      }
    }
  }

  return { teamOpponent, teamBenched, oppKey };
}

// Random round-plan for team-mode: split active teams into quartets (pairs of 2 teams).
export function randomTeamRoundPlan(activeTeams) {
  const shuffled = shuffle(activeTeams);
  const plan = [];
  for (let i = 0; i + 1 < shuffled.length; i += 2) {
    const a = shuffled[i];
    const b = shuffled[i + 1];
    plan.push({
      team1: [a.p1Id, a.p2Id],
      team2: [b.p1Id, b.p2Id],
    });
  }
  return plan;
}

// Cost function for team-mode: opponent cost + ELO balance between teams.
// Partner cost is dropped because the pairs are fixed.
export function calcTeamRoundCost(roundPlan, teamHistory, teams, players) {
  const { teamOpponent, oppKey } = teamHistory;
  let cost = 0;

  for (const match of roundPlan) {
    const tk1 = teamKey(match.team1[0], match.team1[1]);
    const tk2 = teamKey(match.team2[0], match.team2[1]);

    // How many times have these teams met each other? Squared.
    const met = teamOpponent.get(oppKey(tk1, tk2)) || 0;
    cost += met * met * AMERICANO_OPPONENT_COST * 4; // multiplied by 4 to match the scale of the 4-player cost

    // ELO imbalance between the teams
    const pid = (id) => players.find((p) => p.id === id);
    const t1Elo = ((pid(match.team1[0])?.elo || 1500) + (pid(match.team1[1])?.elo || 1500)) / 2;
    const t2Elo = ((pid(match.team2[0])?.elo || 1500) + (pid(match.team2[1])?.elo || 1500)) / 2;
    cost += Math.abs(t1Elo - t2Elo) * AMERICANO_IMBALANCE_COST;
  }

  return cost;
}

// Main algorithm for team-mode
export function generateTeamAmericanoRoundPlan(activeTeams, teamHistory, teams, players) {
  if (activeTeams.length < 2 || activeTeams.length % 2 !== 0) {
    throw new Error(`Antall aktive lag må være delelig på 2 (fikk ${activeTeams.length})`);
  }

  let bestPlan = null;
  let bestCost = Infinity;

  for (let iter = 0; iter < AMERICANO_SEARCH_ITERATIONS; iter++) {
    const plan = randomTeamRoundPlan(activeTeams);
    const cost = calcTeamRoundCost(plan, teamHistory, teams, players);
    if (cost < bestCost) {
      bestCost = cost;
      bestPlan = plan;
      if (cost === 0) break;
    }
  }

  return bestPlan;
}

// Pick which TEAMS should sit out, based on benched history
export function selectBenchedTeams(teams, teamBenched, neededBenchTeams) {
  if (neededBenchTeams === 0) return [];
  const sorted = [...teams].sort((a, b) => {
    const ba = teamBenched.get(teamKey(a.p1Id, a.p2Id)) || 0;
    const bb = teamBenched.get(teamKey(b.p1Id, b.p2Id)) || 0;
    return ba - bb; // fewest benched first
  });
  return sorted.slice(0, neededBenchTeams);
}

// ----------------------------------------------------------------
// AMERICANO STATE — create and update
// ----------------------------------------------------------------

// Default Americano configuration
export const AMERICANO_DEFAULT_CONFIG = {
  name: 'Americano',
  tournamentType: 'americano',
  courts: 2,
  pointsType: 'games_only', // 'games_and_sets' | 'games_only' | 'points'
  endMode: 'total', // 'total' | 'race' | 'bingo' | 'time'
  scoreMode: 'total', // [DEPRECATED — kept for backwards compatibility, use endMode]
  tieBreaker: 'wins_first', // 'wins_first' | 'points_first'
  // Head-to-head: when two players are tied on primary and
  // secondary sorting, we can look at their mutual result to break the tie.
  //   'off'        → h2h ignored (unchanged behaviour — may end in a shared place)
  //   'before'     → h2h checked BEFORE point diff (between primary and point diff)
  //   'after_diff' → h2h checked AFTER point diff, before total points
  //   'after'      → h2h checked AFTER total points (final word before a shared place)
  // Useful in Americano where players meet each other in different pairs
  // — h2h is then based on all matches where they were on opposite teams.
  h2hPosition: 'off',
  pointsPerGame: 32, // total / race target / bingo target (depending on endMode)
  timeMinutes: 15, // used when endMode === 'time'
  finalsFormat: 'cross', // 'cross' (1&3 vs 2&4) | 'top_pair' (1&2 vs 3&4)
  winnerSource: 'finals', // 'finals' (winner of the top final) | 'standings' (1st place in the overall standings)
  kMultiplier: 0.5, // ELO effect reduced to 50% since the matches are shorter than Padel24/single matches
  benchedReward: 'tie', // What do players who sit out a round receive?
  //   'tie'  — draw + average points (0.5 win, fair on both tieBreakers) [DEFAULT]
  //   'avg'  — average points, no win/draw recorded
  //   'win'  — full win + low points (compensating)
  //   'skip' — nothing awarded (not counted as played)
  // ──── Team Americano fields ───────────────────────────────────────────
  // When teamMode === true, the players are kept together as fixed pairs throughout
  // the whole tournament. Standings are computed per TEAM, and both players on
  // the same team get the same ranking. The bench logic applies to whole teams.
  teamMode: false,
  // Array of fixed pairs when teamMode is on: [{ p1Id, p2Id }, ...].
  // Used by generateAmericanoRound to preserve the pairs through rotations,
  // and by calcAmericanoStandings to group players in the standings.
  teams: null,
  // ──── ACTIVITIES fields (sign-up via the Activities module) ──────────
  // activityMode: 'standalone' = run on its own (admin adds players themselves,
  //   as today) | 'activity' = published in Activities with "Wait for sign-up".
  activityMode: 'standalone',
  maxPlayers: null, // max number of registrants (capacity)
  pricePerPlayer: 0, // fee per player (NOK)
  activityDate: null, // date the tournament is held (YYYY-MM-DD, local)
  activityTime: null, // start time (HH:MM)
  activityEndTime: null, // end time (HH:MM) — duration is derived for the booking
  deregisterCutoffHours: 24, // how many hours before start one can deregister
};

// Create a new Americano tournament
export function createAmericanoTournament({ config, players }) {
  if (!Array.isArray(players) || players.length < 4) {
    return { error: 'Minst 4 spillere kreves' };
  }

  const teamMode = config?.teamMode === true;

  // Team Americano: requires an even number (to form pairs). An odd number of teams is OK
  // — then one team has to sit out more often in the rotation, and if a playoff
  // is chosen, the last team in the ranking sits out (or the user can choose
  // "Totalpoeng" as the winner criterion). The engine handles this automatically.
  if (teamMode) {
    if (players.length % 2 !== 0) {
      return { error: 'Team Americano krever et partall antall spillere (par)' };
    }
    if (players.length < 4) {
      return { error: 'Team Americano krever minst 4 spillere (2 lag)' };
    }
    const teams = Array.isArray(config?.teams) ? config.teams : null;
    if (!teams || teams.length !== players.length / 2) {
      return { error: 'Team Americano krever et komplett sett med makkerpar' };
    }
    // Validate that all players are in exactly one pair
    const playerIds = new Set(players.map((p) => p.id));
    const pairedIds = new Set();
    for (const t of teams) {
      if (!t?.p1Id || !t?.p2Id || t.p1Id === t.p2Id) {
        return { error: 'Hvert lag må ha to ulike spillere' };
      }
      if (!playerIds.has(t.p1Id) || !playerIds.has(t.p2Id)) {
        return { error: 'Lag inneholder spillere som ikke er i turneringen' };
      }
      if (pairedIds.has(t.p1Id) || pairedIds.has(t.p2Id)) {
        return { error: 'En spiller kan kun være på ett lag' };
      }
      pairedIds.add(t.p1Id);
      pairedIds.add(t.p2Id);
    }
  }

  const tournamentType = teamMode ? 'team_americano' : 'americano';

  return {
    id: `am_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
    type: 'americano',
    config: { ...AMERICANO_DEFAULT_CONFIG, ...config, tournamentType, teamMode },
    // Store startElo per player — used to show the ELO change in the history
    players: players.map((p) => ({ ...p, startElo: p.elo })),
    matches: [], // all played matches chronologically
    currentRound: 0, // 0 = no rounds generated yet
    standings: {}, // { playerId: { wins, losses, points, gamesWon, gamesLost } }
    status: 'setup', // 'setup' | 'playing' | 'finals' | 'done' | 'awaiting_signup' (Activities — waiting for sign-up)
    // ACTIVITIES: sign-ups when the tournament is run via the Activities module.
    // [{ playerId, status: 'confirmed' | 'waitlist', paidStatus: 'pending'|'paid'|'refunded'|'free', signedUpAt }]
    registrations: [],
    finalMatches: [], // generated when the admin starts the finals
    startedAt: null,
    completedAt: null,
  };
}

// Generate a new round in an ongoing americano
// Handles odd counts by benching some players — those who have been benched the LEAST so far
export function generateAmericanoRound(americano) {
  const { players, matches, config, currentRound } = americano;

  // ──── Team Americano: dedicated round generation ────────────────────────
  // Keeps the pairs fixed; quartets consist of 2 teams vs 2 teams.
  if (config?.teamMode === true) {
    return generateTeamAmericanoRound(americano);
  }

  // Filter out injured players — they do not take part in new rounds.
  // Injured players are kept in the tournament.players array so their history and
  // ELO are preserved, but they are not rotated into new matches.
  const activePlayers = players.filter((p) => !p.injured);
  const playerIds = activePlayers.map((p) => p.id);

  // Build history from all previous matches (but not from finals)
  const regularMatches = matches.filter((m) => !m.isFinal);
  const history = buildAmericanoHistory(regularMatches);

  // Decide who plays this round
  // Need a count divisible by 4 — bench the rest
  const totalCourts = config.courts || 1;
  const maxPlayersThisRound = Math.min(playerIds.length, totalCourts * 4);
  // Active: must be divisible by 4 and ≤ maxPlayersThisRound
  const activeCount = Math.floor(maxPlayersThisRound / 4) * 4;
  const benchCount = playerIds.length - activeCount;

  let benched = [];
  let activePlayerIds = playerIds;

  if (benchCount > 0) {
    // Pick those who have been benched the least — they should sit out now (to even out)
    benched = selectBenched(playerIds, history.benchCount, benchCount);
    activePlayerIds = playerIds.filter((id) => !benched.includes(id));
  }

  // Generate the optimal round plan (only active players are used for ELO matching)
  const courtsPlan = generateAmericanoRoundPlan(activePlayerIds, history, activePlayers);

  // Build the match objects
  const roundNum = currentRound + 1;
  const newMatches = courtsPlan.map((plan, idx) => ({
    id: `am_r${roundNum}_c${idx + 1}`,
    round: roundNum,
    court: idx + 1,
    team1: { p1Id: plan.team1[0], p2Id: plan.team1[1] },
    team2: { p1Id: plan.team2[0], p2Id: plan.team2[1] },
    score1: null,
    score2: null,
    sets: [], // for 'games_and_sets' mode
    completed: false,
    isFinal: false,
    benched, // who sits out this round
  }));

  return newMatches;
}

// ============================================================
// TEAM AMERICANO — round generation
// ============================================================
// Keeps the pairs from config.teams fixed. Each team plays as a unit.
// Injured player → the whole team sits out (the pair principle).
export function generateTeamAmericanoRound(americano) {
  const { players, matches, config, currentRound } = americano;
  const teams = Array.isArray(config?.teams) ? config.teams : [];

  // Find injured players — if a pair has an injured player, the team sits out.
  const injuredSet = new Set(players.filter((p) => p.injured).map((p) => p.id));
  const activeTeams = teams.filter((t) => !injuredSet.has(t.p1Id) && !injuredSet.has(t.p2Id));

  // Build history from all previous matches (but not finals)
  const regularMatches = matches.filter((m) => !m.isFinal);
  const teamHistory = buildTeamHistory(regularMatches, teams);

  // Number of teams that can actually play this round:
  // - must be divisible by 2 (a pair of teams = one match)
  // - must fit the number of courts (one court = 2 teams = 4 players)
  const totalCourts = config.courts || 1;
  const maxTeamsThisRound = Math.min(activeTeams.length, totalCourts * 2);
  const activeTeamCount = Math.floor(maxTeamsThisRound / 2) * 2;
  const benchTeamCount = activeTeams.length - activeTeamCount;

  let benchedTeams = [];
  let playingTeams = activeTeams;

  if (benchTeamCount > 0) {
    benchedTeams = selectBenchedTeams(activeTeams, teamHistory.teamBenched, benchTeamCount);
    const benchedKeys = new Set(benchedTeams.map((t) => teamKey(t.p1Id, t.p2Id)));
    playingTeams = activeTeams.filter((t) => !benchedKeys.has(teamKey(t.p1Id, t.p2Id)));
  }

  // The benched list is stored as player ids to be compatible with
  // existing UI/history code (which expects player ids).
  const benchedPlayerIds = benchedTeams.flatMap((t) => [t.p1Id, t.p2Id]);

  // Generate the plan over pairs of teams
  const courtsPlan = generateTeamAmericanoRoundPlan(playingTeams, teamHistory, teams, players);

  const roundNum = currentRound + 1;
  const newMatches = courtsPlan.map((plan, idx) => ({
    id: `am_r${roundNum}_c${idx + 1}`,
    round: roundNum,
    court: idx + 1,
    team1: { p1Id: plan.team1[0], p2Id: plan.team1[1] },
    team2: { p1Id: plan.team2[0], p2Id: plan.team2[1] },
    score1: null,
    score2: null,
    sets: [],
    completed: false,
    isFinal: false,
    benched: benchedPlayerIds,
  }));

  return newMatches;
}

// The ranking order a tournament uses — ONE answer for the player ranking,
// the team ranking and anything else that sorts standings.
//
// Uses `config.rankingOrder` when it is a non-empty array. Otherwise, for
// tournaments created before the drag-and-drop order existed, rebuilds it
// from the old `tieBreaker` / `h2hPosition` fields; and with neither, the
// default (DEFAULT_RANKING_AMERICANO — the same list the setup UI offers).
export function resolveAmericanoRankingOrder(config: any): Array<{ id: string; enabled: boolean }> {
  const configured = config?.rankingOrder;
  if (Array.isArray(configured) && configured.length > 0) return configured;

  if (config?.tieBreaker || config?.h2hPosition) {
    const oldTieBreaker = config.tieBreaker || 'points_first';
    const oldH2hPosition = config.h2hPosition || 'off';
    const primary =
      oldTieBreaker === 'wins_first'
        ? [
            { id: 'wins', enabled: true },
            { id: 'pointsDiff', enabled: true },
            { id: 'points', enabled: true },
          ]
        : [
            { id: 'points', enabled: true },
            { id: 'wins', enabled: true },
            { id: 'pointsDiff', enabled: true },
          ];
    const order = [...primary];
    const h2hEntry = { id: 'h2h', enabled: oldH2hPosition !== 'off' };
    if (oldH2hPosition === 'before') order.splice(1, 0, h2hEntry);
    else if (oldH2hPosition === 'after_diff') order.splice(2, 0, h2hEntry);
    else if (oldH2hPosition === 'after') order.push(h2hEntry);
    else /* 'off' */ order.push({ ...h2hEntry, enabled: false });
    return order;
  }

  return DEFAULT_RANKING_AMERICANO;
}

// Calculate standings based on all played matches
//
// Bench compensation: players who have sat out receive, per ROUND sat out,
// whatever `config.benchedReward` says (average points and a draw by
// default). A round is counted once however many courts it had — every
// match in a round carries the same `benched` list, so the list is read from
// one match per round, not from every match (#494).
export function calcAmericanoStandings(americano) {
  const { players, matches, config } = americano;
  const standings: Record<string, any> = {};

  for (const p of players) {
    standings[p.id] = {
      playerId: p.id,
      wins: 0,
      losses: 0,
      draws: 0,
      points: 0, // total points (games or raw points)
      pointsAgainst: 0,
      gamesPlayed: 0,
      benchedRounds: 0,
    };
  }

  // First: go through all regular (non-final) matches, compute average points per match
  // to use as compensation for benched players
  const regularMatches = matches.filter((m) => m.completed && !m.isFinal);

  // Compute average points per match across all players
  let totalPointsScored = 0;
  let totalMatchSides = 0; // each match has 2 teams (4 player participations)

  for (const m of regularMatches) {
    const s1 = m.score1 || 0;
    const s2 = m.score2 || 0;
    totalPointsScored += s1 + s2;
    totalMatchSides += 2; // number of team sides
  }

  const avgPointsPerSide = totalMatchSides > 0 ? totalPointsScored / totalMatchSides : 0;

  // Count benched ROUNDS per player. One round has the same benched list on
  // all its courts, so the list is taken from the first match of each round.
  const benchedByRound = new Map(); // 'roundNum' -> benched array
  for (const m of regularMatches) {
    if (!benchedByRound.has(m.round) && Array.isArray(m.benched)) {
      benchedByRound.set(m.round, m.benched);
    }
  }

  const benchedRoundCount = new Map();
  for (const [, benchedList] of benchedByRound) {
    for (const id of benchedList) {
      benchedRoundCount.set(id, (benchedRoundCount.get(id) || 0) + 1);
    }
  }

  // Count points and wins from all regular matches
  for (const m of regularMatches) {
    const s1 = m.score1 || 0;
    const s2 = m.score2 || 0;
    const t1Won = s1 > s2;
    const t2Won = s2 > s1;

    for (const id of [m.team1.p1Id, m.team1.p2Id]) {
      const s = standings[id];
      if (!s) continue;
      s.gamesPlayed += 1;
      s.points += s1;
      s.pointsAgainst += s2;
      if (t1Won) s.wins += 1;
      else if (t2Won) s.losses += 1;
      else s.draws += 1;
    }

    for (const id of [m.team2.p1Id, m.team2.p2Id]) {
      const s = standings[id];
      if (!s) continue;
      s.gamesPlayed += 1;
      s.points += s2;
      s.pointsAgainst += s1;
      if (t2Won) s.wins += 1;
      else if (t1Won) s.losses += 1;
      else s.draws += 1;
    }
  }

  // Award bench compensation based on config.benchedReward.
  // Injured players do NOT get a benched reward (they are out, not waiting) —
  // benchedRoundCount is checked against players[].injured.
  const benchedReward = config.benchedReward || 'tie';
  const playerById = new Map<any, any>(americano.players.map((p: any) => [p.id, p]));

  for (const [id, roundCount] of benchedRoundCount) {
    const s = standings[id];
    if (!s) continue;
    s.benchedRounds = roundCount;

    const player = playerById.get(id);
    const isInjured = player?.injured === true;

    if (isInjured) {
      // Injured player: no reward for benched rounds after the injury occurred.
      // We would only award a reward for benched rounds BEFORE injuredAt, but since we don't
      // track per-round timing here, and injured players don't get new benched rounds
      // (filtered out by generateRound), we simplify: no reward at all.
      // The player keeps points from matches actually played.
      continue;
    }

    // A REWARDED ROUND COUNTS AS PLAYED (#648, decided 2026-10-09). The table
    // reads S/U/T as a breakdown of Sp; a reward that bumped S or U without
    // Sp made the columns disagree with each other. 'skip' rewards nothing
    // and so counts nothing — the 💺 badge still shows the round.
    switch (benchedReward) {
      case 'skip':
        // No points/win — not counted. benchedRounds is still recorded
        // for display, but has no effect on the standings.
        break;
      case 'avg':
        // Only average points, no win/draw
        s.points += Math.round(avgPointsPerSide * roundCount);
        s.pointsAgainst += Math.round(avgPointsPerSide * roundCount);
        s.gamesPlayed += roundCount;
        break;
      case 'win':
        // Full win + low points (compensating). We give 60% of the average points
        // for the "loss" side (opponent), so the player gets a positive point diff.
        s.points += Math.round(avgPointsPerSide * roundCount);
        s.pointsAgainst += Math.round(avgPointsPerSide * 0.6 * roundCount);
        s.wins += roundCount;
        s.gamesPlayed += roundCount;
        break;
      case 'tie':
      default:
        // Draw + average points (DEFAULT). A 0.5 win counts on both tieBreakers.
        s.points += Math.round(avgPointsPerSide * roundCount);
        s.pointsAgainst += Math.round(avgPointsPerSide * roundCount);
        s.draws += roundCount;
        s.gamesPlayed += roundCount;
        break;
    }
  }

  // Convert to a sorted array — applies the tiebreaker.
  // Injured players are ALWAYS sorted at the bottom, regardless of points.
  const ranked = Object.values(standings).map((s) => {
    const player = playerById.get(s.playerId);
    return {
      ...s,
      pointDiff: s.points - s.pointsAgainst,
      // A draw is half a win (#568). `wins` stays the integer the table shows;
      // `winCredit` is what the «wins» ranking criterion compares, so one win
      // plus one draw ranks ahead of one win plus one loss. The benched reward
      // 'tie' has always promised exactly this («0.5 seier») — now it delivers.
      winCredit: s.wins + 0.5 * s.draws,
      injured: player?.injured === true,
    };
  });

  // ============================================================
  // HEAD-TO-HEAD
  // ============================================================
  // Build a h2h table: for each player pair (A, B), compute how many
  // times A beat B and vice versa. In Americano players meet on
  // different teams — so we look at all regular matches where A and B were
  // opponents (on opposite teams). Each such match gives +1 to the winner.
  //
  // Always computed (even if h2h is not enabled in rankingOrder) — it
  // costs little and simplifies the logic below.
  const h2hWins = {};
  Object.keys(standings).forEach((id) => {
    h2hWins[id] = {};
  });

  for (const m of regularMatches) {
    const s1 = m.score1 || 0;
    const s2 = m.score2 || 0;
    if (s1 === s2) continue; // a draw produces no h2h outcome

    const team1 = [m.team1?.p1Id, m.team1?.p2Id].filter(Boolean);
    const team2 = [m.team2?.p1Id, m.team2?.p2Id].filter(Boolean);
    const winners = s1 > s2 ? team1 : team2;
    const losers = s1 > s2 ? team2 : team1;

    // Each winner has beaten each of the two losers (in this match)
    for (const w of winners) {
      if (!h2hWins[w]) h2hWins[w] = {};
      for (const l of losers) {
        h2hWins[w][l] = (h2hWins[w][l] || 0) + 1;
      }
    }
  }

  // Helper: comparison per criterion for Americano.
  // Returns negative if a comes BEFORE b, positive if b before a, 0 = equal.
  const americanoCompareBy = (criterion, a, b) => {
    if (criterion === 'wins') {
      // Draws count half (#568) — see `winCredit` above.
      return (b.winCredit || 0) - (a.winCredit || 0);
    }
    if (criterion === 'points') {
      return (b.points || 0) - (a.points || 0);
    }
    if (criterion === 'pointsDiff') {
      return (b.pointDiff || 0) - (a.pointDiff || 0);
    }
    if (criterion === 'h2h') {
      const aWins = h2hWins[a.playerId]?.[b.playerId] || 0;
      const bWins = h2hWins[b.playerId]?.[a.playerId] || 0;
      return bWins - aWins;
    }
    return 0;
  };

  const rankingOrder = resolveAmericanoRankingOrder(config);

  // HEAD-TO-HEAD ORDERS, IT DOES NOT RANK (#648, decided 2026-10-09).
  //
  // The ORDINAL criteria — wins (with draws as half), point difference,
  // points — are properties of one player and compare transitively. Head to
  // head is a property of a PAIR: A over B says nothing about A against C,
  // and a cycle (A beat B, B beat C, C beat A) has no consistent order at
  // all. Used as a ranking criterion between neighbours, it gave three
  // players level on everything different places depending on who happened
  // to sit between them, and sorted differently depending on input order.
  //
  // So the ordinal criteria decide the PLACE, in the configured order, and
  // h2h decides only the ORDER WITHIN a group that shares a place — always
  // last, whatever position the configuration gave it. A player level on all
  // the ordinal criteria shares the place either way.
  const ordinalOrder = rankingOrder.filter((item) => item.enabled && item.id !== 'h2h');
  const h2hEnabled = rankingOrder.some((item) => item.enabled && item.id === 'h2h');

  // ── AND IT ORDERS AS A GROUP, NOT PAIRWISE (#720 review) ────────
  //
  // The comment above has it right; the first implementation did not finish the
  // job. Using h2h as the final PAIRWISE comparator inside `sort` leaves the
  // comparator non-transitive, and `Array.prototype.sort` with an inconsistent
  // comparator produces an order that depends on the input order and on the
  // engine's sort implementation — which is exactly what «sorted differently
  // depending on input order» says must not happen.
  //
  // So a tie group is ordered by a SCALAR instead: wins against the others in
  // that group, minus losses to them. A scalar is transitive by construction, so
  // the result cannot depend on who happened to sit next to whom.
  //
  // A TRUE CYCLE SCORES ZERO FOR EVERYONE — A beat B, B beat C, C beat A gives
  // each of them +1 and −1 — which is the honest answer: head to head has no
  // opinion there. The deterministic fallback then decides, so the display is
  // stable rather than arbitrary.
  const ordinalCompare = (a, b) => {
    // Injured players are ALWAYS sorted at the bottom, regardless of points/wins
    if (a.injured !== b.injured) return a.injured ? 1 : -1;
    for (const item of ordinalOrder) {
      const cmp = americanoCompareBy(item.id, a, b);
      if (cmp !== 0) return cmp;
    }
    return 0;
  };

  // THE STABLE KEY, so equal rows have ONE order rather than whichever the input
  // happened to carry. `playerId` is arbitrary as a ranking, and that is the
  // point: it decides nothing, it only stops the answer wobbling.
  const byId = (a, b) => String(a.playerId).localeCompare(String(b.playerId));

  ranked.sort((a, b) => ordinalCompare(a, b) || byId(a, b));

  if (h2hEnabled) {
    let from = 0;
    while (from < ranked.length) {
      let to = from + 1;
      while (to < ranked.length && ordinalCompare(ranked[from], ranked[to]) === 0) to += 1;
      if (to - from > 1) {
        const group = ranked.slice(from, to);
        const score = new Map(
          group.map((s) => [
            s.playerId,
            group.reduce(
              (sum, other) =>
                other.playerId === s.playerId
                  ? sum
                  : sum +
                    (h2hWins[s.playerId]?.[other.playerId] || 0) -
                    (h2hWins[other.playerId]?.[s.playerId] || 0),
              0,
            ),
          ]),
        );
        group.sort(
          (a, b) => (score.get(b.playerId) || 0) - (score.get(a.playerId) || 0) || byId(a, b),
        );
        for (let k = 0; k < group.length; k += 1) ranked[from + k] = group[k];
      }
      from = to;
    }
  }

  // Assign rank — tied players get the same rank (a shared place).
  // E.g. if two have the same points/wins/diff: both get rank 1, the next gets rank 3.
  // Injured players are excluded from rank computation and get rank=null (shown as '—').
  ranked.forEach((s, i) => {
    if (s.injured) {
      s.rank = null;
      return;
    }
    if (i === 0) {
      s.rank = 1;
      return;
    }
    const prev = ranked[i - 1];
    // Equal on every ORDINAL criterion → the same place. Comparing with the
    // row above is enough because the list is sorted on exactly those
    // criteria, so equality is transitive along the run.
    const isTie = ordinalOrder.every((item) => americanoCompareBy(item.id, s, prev) === 0);
    s.rank = isTie ? prev.rank : i + 1;
  });

  // ──── Team Americano: recompute rank as TEAM rank ─────────────────────
  // With fixed pairs, both players on the same team have identical numbers, so they
  // end up next to each other and get the same rank. But the next team would
  // get rank 3 (since the index is 2), not 2. We want TEAM ranking: 1, 2, 3.
  // Strategy: go through the list, and each time we hit a new "team group"
  // (a rank that jumps up), set rank to previous_team_rank + 1.
  if (config?.teamMode === true && Array.isArray(config?.teams)) {
    // Build a playerId → teamKey map for quick lookup
    const playerToTeam = new Map();
    for (const t of config.teams) {
      const tk = teamKey(t.p1Id, t.p2Id);
      playerToTeam.set(t.p1Id, tk);
      playerToTeam.set(t.p2Id, tk);
    }

    let currentTeamRank = 0;
    let prevTeamKey = null;
    let prevPlayerRank = null;

    for (const s of ranked) {
      if (s.injured) continue;
      const tk = playerToTeam.get(s.playerId);
      if (tk && tk === prevTeamKey) {
        // Same team as the previous player — reuse the team rank
        s.rank = currentTeamRank;
      } else {
        // New team (or first player) — check whether it is tied
        // with the previous team based on the same rankingOrder tie logic.
        // For team-mode, a tie between TEAMS is rare (both players
        // have the same numbers), but we respect it if it should occur.
        if (prevPlayerRank !== null && s.rank === prevPlayerRank) {
          // Player rank tied with the previous team → share the team rank
          s.rank = currentTeamRank;
        } else {
          currentTeamRank += 1;
          s.rank = currentTeamRank;
        }
        prevTeamKey = tk;
      }
      prevPlayerRank = s.rank;
    }
  }

  return ranked;
}

// Generate final matches based on the current standings
// 1&3 vs 2&4 (top final), 5&7 vs 6&8 (3rd-place final), etc.
export function generateAmericanoFinals(americano) {
  const standings = calcAmericanoStandings(americano);

  // ──── Team Americano: team finals ─────────────────────────────────
  // With fixed pairs, finalsFormat is not meaningful — we play directly
  // team vs team based on the final ranking: team 1 vs team 2 (gold final),
  // team 3 vs team 4 (bronze final), etc. Each "rank" is a whole team,
  // represented by 2 players with the same rank.
  if (americano.config?.teamMode === true) {
    return generateTeamAmericanoFinals(americano, standings);
  }

  // Injured players sit at the bottom of the standings with rank null. They
  // are out of the tournament, not waiting for a court: the team and club
  // variants already leave them out of the finals, and so does this one.
  const seeded = standings.filter((s) => !s.injured && s.rank != null);

  const finalMatches = [];
  const matchCount = Math.floor(seeded.length / 4);

  // Format: 'cross' (1&3 vs 2&4) or 'top_pair' (1&2 vs 3&4)
  const finalsFormat = americano.config?.finalsFormat || 'cross';

  for (let i = 0; i < matchCount; i++) {
    const offset = i * 4;
    const p1 = seeded[offset]; // 1st
    const p2 = seeded[offset + 1]; // 2nd
    const p3 = seeded[offset + 2]; // 3rd
    const p4 = seeded[offset + 3]; // 4th

    // Determine teams based on the format
    let team1, team2;
    if (finalsFormat === 'top_pair') {
      // 1&2 vs 3&4 — the top pair meets the bottom pair (ranked pairs)
      team1 = { p1Id: p1.playerId, p2Id: p2.playerId };
      team2 = { p1Id: p3.playerId, p2Id: p4.playerId };
    } else {
      // 1&3 vs 2&4 — crossed, gives more balanced teams (default)
      team1 = { p1Id: p1.playerId, p2Id: p3.playerId };
      team2 = { p1Id: p2.playerId, p2Id: p4.playerId };
    }

    finalMatches.push({
      id: `am_final_${i + 1}`,
      round: 0,
      court: i + 1,
      finalNumber: i + 1, // 1 = top final (1st-2nd place), 2 = 5th-6th place, etc.
      team1,
      team2,
      score1: null,
      score2: null,
      sets: [],
      completed: false,
      isFinal: true,
      finalsFormat, // Store the format on the match so it is traceable
      preStanding: [p1.playerId, p2.playerId, p3.playerId, p4.playerId],
    });
  }

  return finalMatches;
}

// ============================================================
// TEAM AMERICANO — generate finals based on team ranking
// ============================================================
// The final ranking in team-mode is per TEAM (both players on the same team
// have the same rank). We group the ranked list into teams and play match-ups
// neighbor-vs-neighbor: team 1 vs team 2 → gold final, team 3 vs team 4 → bronze final.
export function generateTeamAmericanoFinals(americano, standings) {
  const finalMatches = [];

  // Get unique teams in order (based on rank). Each team is { p1Id, p2Id, rank }.
  const seenTeamKeys = new Set();
  const orderedTeams = [];
  for (const s of standings) {
    if (s.injured || s.rank == null) continue;
    // Find the partner via config.teams
    const teams = americano.config?.teams || [];
    const team = teams.find((t) => t.p1Id === s.playerId || t.p2Id === s.playerId);
    if (!team) continue;
    const tk = teamKey(team.p1Id, team.p2Id);
    if (seenTeamKeys.has(tk)) continue;
    seenTeamKeys.add(tk);
    orderedTeams.push({ p1Id: team.p1Id, p2Id: team.p2Id, rank: s.rank });
  }

  // Pair up teams neighbor-vs-neighbor. With an odd number, the last team sits out.
  const finalCount = Math.floor(orderedTeams.length / 2);
  for (let i = 0; i < finalCount; i++) {
    const offset = i * 2;
    const teamA = orderedTeams[offset];
    const teamB = orderedTeams[offset + 1];

    finalMatches.push({
      id: `am_final_${i + 1}`,
      round: 0,
      court: i + 1,
      finalNumber: i + 1, // 1 = gold final, 2 = bronze final, ...
      team1: { p1Id: teamA.p1Id, p2Id: teamA.p2Id },
      team2: { p1Id: teamB.p1Id, p2Id: teamB.p2Id },
      score1: null,
      score2: null,
      sets: [],
      completed: false,
      isFinal: true,
      // Preserve the team ranking on the match for traceability
      preStanding: [teamA.p1Id, teamA.p2Id, teamB.p1Id, teamB.p2Id],
    });
  }

  return finalMatches;
}

// ------------------------------------------------------------
// FILL PLACEHOLDER — swap a reserved slot for a real player
// Unlike the replacement flow, no history is stored.
// ------------------------------------------------------------
export function fillPlaceholderInAmericano(tournament, placeholderId, newPlayer) {
  if (!tournament) return tournament;

  // Swap the id in all match slots (placeholder-id → newPlayer.id)
  const replaceId = (id) => (id === placeholderId ? newPlayer.id : id);
  const newMatches = (tournament.matches || []).map((m) => ({
    ...m,
    team1: { p1Id: replaceId(m.team1.p1Id), p2Id: replaceId(m.team1.p2Id) },
    team2: { p1Id: replaceId(m.team2.p1Id), p2Id: replaceId(m.team2.p2Id) },
  }));

  // Replace the placeholder with the new player in the players array
  const placeholderRef = (tournament.players || []).find((p) => p.id === placeholderId);
  const newPlayers = (tournament.players || []).map((p) =>
    p.id === placeholderId ? { ...newPlayer } : p,
  );

  // THE LOG STORES WHAT HAPPENED, NOT A SENTENCE (#519) — the same entry
  // fillPlaceholderInTournament writes; see replacePlayerInAmericano.
  const logItem = {
    time: new Date().toISOString(),
    code: 'placeholderFilled',
    vars: {
      placeholder: placeholderRef?.name || '?',
      player: newPlayer.name,
      elo: String(newPlayer.elo),
    },
  };

  return {
    ...tournament,
    matches: newMatches,
    players: newPlayers,
    log: [...(tournament.log || []), logItem],
  };
}

// ------------------------------------------------------------
// REPLACE PLAYER — swap a player (e.g. on injury) in an active tournament
// Only swaps in matches that do NOT yet have a result. Finished matches stand.
// Stores replacement history on the match object so it can be shown in the UI.
// ------------------------------------------------------------
export function replacePlayerInAmericano(tournament, originalId, replacementPlayer) {
  if (!tournament) return tournament;

  const replacementTimestamp = Date.now();

  // How many matches had the ORIGINAL played when the swap happened?
  // (used to hide the substitute marker if the swap was before the first match)
  const originalCompletedCount = (tournament.matches || []).filter((m) => {
    if (!m.completed) return false;
    const ids = [m.team1.p1Id, m.team1.p2Id, m.team2.p1Id, m.team2.p2Id];
    return ids.includes(originalId);
  }).length;

  // Replace the id in the match slots and store replacement info on the match itself
  const newMatches = (tournament.matches || []).map((m) => {
    if (m.completed) return m; // already played — don't touch
    const hasOriginal = [m.team1.p1Id, m.team1.p2Id, m.team2.p1Id, m.team2.p2Id].includes(
      originalId,
    );
    if (!hasOriginal) return m;
    const replaceId = (id) => (id === originalId ? replacementPlayer.id : id);
    return {
      ...m,
      team1: { p1Id: replaceId(m.team1.p1Id), p2Id: replaceId(m.team1.p2Id) },
      team2: { p1Id: replaceId(m.team2.p1Id), p2Id: replaceId(m.team2.p2Id) },
      // Store replacement info so the UI can show a substitute badge
      replacements: [
        ...(m.replacements || []),
        {
          originalId,
          replacementId: replacementPlayer.id,
          timestamp: replacementTimestamp,
          originalCompletedCount,
        },
      ],
    };
  });

  // Add the substitute to the players array if not already there
  const existing = (tournament.players || []).find((p) => p.id === replacementPlayer.id);
  const newPlayers = existing
    ? tournament.players
    : [...(tournament.players || []), { ...replacementPlayer }];

  const originalRef = (tournament.players || []).find((p) => p.id === originalId);
  // THE LOG STORES WHAT HAPPENED, NOT A SENTENCE (#519). The words are the
  // catalog's (`tournament.log.event.*`, apps/web), decided by whoever reads
  // the log. A missing name is a neutral «?» rather than a Norwegian fallback
  // word, and ELO is a string so it renders exactly as before, ungrouped.
  // «1 kamp» / «N kamper» is two codes, as the catalog does for its other
  // plurals, because a template has no logic.
  const logItem = {
    time: new Date().toISOString(),
    code: originalCompletedCount === 1 ? 'americanoSubstitutionOne' : 'americanoSubstitutionMany',
    vars: {
      original: originalRef?.name || '?',
      replacement: replacementPlayer.name,
      eloFrom: String(originalRef?.elo || '?'),
      eloTo: String(replacementPlayer.elo),
      round: String(tournament.currentRound || '?'),
      count: originalCompletedCount,
    },
  };

  return {
    ...tournament,
    matches: newMatches,
    players: newPlayers,
    replacements: [
      ...(tournament.replacements || []),
      {
        id: `repl_${replacementTimestamp}`,
        originalId,
        replacementId: replacementPlayer.id,
        timestamp: replacementTimestamp,
        round: tournament.currentRound,
        originalCompletedCount,
      },
    ],
    log: [...(tournament.log || []), logItem],
  };
}

// Export the americano engine

// ============================================================
// MEXICANO ENGINE — standings-based matchmaking
// ============================================================
//
// Mexicano is like Americano (a rotating-partner format with 4 players per court,
// flat format with tournament.matches and config), but where Americano tries
// to let everyone play with everyone, Mexicano bases matchmaking on the CURRENT STANDINGS.
//
// Sorting: after each round players are sorted by points (descending). The top 4
// play on court 1, the next 4 on court 2, etc.
//
// The "Pool" concept (variation):
//   Pool 4 = pure classic: the top 4 always meet on court 1 (the standings lock it)
//   Pool 6 = court 1 can pick 4 of the top 6 — the algorithm chooses a 4-constellation
//            with the least repetition of partner pairs
//   Pool 8 = court 1 can pick 4 of the top 8 — the most variation, but 7th/8th place
//            can end up on court 1 (sacrificing level precision)
//
// Pair constellation: within 4 players there are 3 possible pair distributions:
//   K2 = 1+3 vs 2+4 (classic Mexicano, the most even)  ← preferred
//   K3 = 1+4 vs 2+3 (even alternative)
//   K1 = 1+2 vs 3+4 (skewed, avoided)
// The algorithm chooses K2 if no partner pair has played together before, otherwise K3,
// otherwise the least-reused (K1 last).
//
// Randomness:
//   - config.randomFirstRounds: the first X rounds are entirely random (default 1)
//   - config.randomEveryNthRound: every Nth round is random (default 0 = never)
//   - Per-round override: opts.forceRandom = true (for the "Random round now" button)
//
// Bench balancing: identical to Americano — the fewest benched rounds
// are left over (they should PLAY), those with the most benched rounds play.
//
// Finals and standings: reuse the Americano functions (same data structure).
// ============================================================

// Allowed pool sizes
export const MEXICANO_POOL_SIZES = [4, 6, 8];

// Smart default pool based on the number of active players
// - 8 players: only pool 4 possible
// - 12 players: pool 4 default (pool 6/8 would take too large a share)
// - 16+ players: pool 6 default
export function defaultMexicanoPoolSize(numPlayers) {
  if (numPlayers < 12) return 4;
  if (numPlayers < 16) return 4;
  return 6;
}

// Which pool sizes are valid for a given number of players?
// Rule: the pool must be ≤ half the active players (otherwise court 2
// and onwards degenerate — too few players left).
export function validMexicanoPoolSizes(numPlayers) {
  return MEXICANO_POOL_SIZES.filter((p) => p <= Math.floor(numPlayers / 2) || p === 4);
}

// Generate all k-combinations of an array
export function mexicanoCombinations(arr, k) {
  if (k === 0) return [[]];
  if (arr.length < k) return [];
  const [first, ...rest] = arr;
  const withFirst = mexicanoCombinations(rest, k - 1).map((c) => [first, ...c]);
  const withoutFirst = mexicanoCombinations(rest, k);
  return [...withFirst, ...withoutFirst];
}

// Score a 4-player combination based on how "reused" it is.
// Lower = more varied. Partner repetition is weighted 10x more strongly than opponent.
export function scoreMexicanoCombo(comboIds, partnerCount, opponentCount, key) {
  let score = 0;
  for (let i = 0; i < 4; i++) {
    for (let j = i + 1; j < 4; j++) {
      const a = comboIds[i],
        b = comboIds[j];
      const p = partnerCount.get(key(a, b)) || 0;
      const o = opponentCount.get(key(a, b)) || 0;
      score += p * p * 10; // squared + heavy weight on partner repetition
      score += o * o * 1; // light weight on opponent repetition
    }
  }
  return score;
}

// Pick 4 of N candidates with smart anti-repetition.
// Chooses the 4-constellation with the lowest score (fewest repetitions).
// On a tie: it is drawn AT RANDOM among the tied ones — this gives variation in
// which 4 of the pool end up on the "pure" court vs the "rest" court.
//
// NB: In round 1 (no history) all combinations have the same score 0,
// so the choice is entirely random. This means the rest court *can* get strong
// players. The algorithm compensates by sorting courts on average rank afterwards
// (see generatePool6Plan), so the rest court is always placed correctly by level.
export function pickMexicanoFour(candidateIds, partnerCount, opponentCount, key) {
  if (candidateIds.length === 4) return candidateIds;

  const combos = mexicanoCombinations(candidateIds, 4);
  if (combos.length === 0) return candidateIds.slice(0, 4);

  // Score all and sort
  const scored = combos.map((c) => ({
    c,
    s: scoreMexicanoCombo(c, partnerCount, opponentCount, key),
  }));
  scored.sort((a, b) => a.s - b.s);

  // Pick at random among those with the LOWEST score (tie handling — gives variation)
  const minScore = scored[0].s;
  const bestCombos = scored.filter((x) => x.s === minScore);
  return bestCombos[Math.floor(Math.random() * bestCombos.length)].c;
}

// Pick the pair constellation for 4 players with smart anti-repetition.
// 3 possible pairings:
//   K2: a+c vs b+d
//   K3: a+d vs b+c
//   K1: a+b vs c+d
// Chooses the one with the fewest previous partner pairs. On a tie: random (gives variation).
export function pickMexicanoPairing(four, partnerCount, key) {
  const [a, b, c, d] = four;

  const variants = [
    { team1: [a, c], team2: [b, d], type: 'K2' },
    { team1: [a, d], team2: [b, c], type: 'K3' },
    { team1: [a, b], team2: [c, d], type: 'K1' },
  ];

  // Score = sum of squared partner repetition for both pairs
  const scored = variants.map((v) => {
    const p1 = partnerCount.get(key(v.team1[0], v.team1[1])) || 0;
    const p2 = partnerCount.get(key(v.team2[0], v.team2[1])) || 0;
    return { ...v, s: p1 * p1 + p2 * p2 };
  });
  scored.sort((x, y) => x.s - y.s);

  // Random among the tied ones
  const minScore = scored[0].s;
  const best = scored.filter((x) => x.s === minScore);
  return best[Math.floor(Math.random() * best.length)];
}

// Pool 6 with rest-group algorithm:
//
// Splits players into pools of 6 from the top of the standings. From each pool:
//   - Pick 4 (smart anti-rep, random among the tied ones)
//   - 2 leftovers
// Leftovers from two neighboring pools (4 players) become a "rest group" that
// is placed on a court between the two original pools by level.
//
// Example 16 players / 4 courts:
//   Pool A (rank 1-6)   → 4 to court 1, 2 leftover
//   Pool B (rank 7-12)  → 4 to court 3, 2 leftover
//   Rest group (4)      → court 2 (between A and B by level)
//   Bottom (13-16)      → court 4
//
// Example 12 players / 3 courts:
//   Pool A (rank 1-6)   → 4 to court 1, 2 leftover
//   Bottom (7-12)       → court 3
//   The rest group only gets 2 from A — fills with the top of Bottom:
//     No, that is too complicated. Fallback: if the math doesn't work out,
//     use classic pool 4 for the rest.
//
// Returns: array of { ids: [4 players sorted by rank], court: 1+ }
export function generatePool6Plan(sortedIds, history) {
  const { partnerCount, opponentCount, key } = history;
  const blocks = []; // { ids: [4], avgRank, kind: 'pure' | 'rest' | 'bottom' }
  const remaining = [...sortedIds];
  let purePoolStart = 0;
  let restCarry = null; // {ids: [2], fromPoolStart: number}

  while (remaining.length >= 6) {
    const pool = remaining.slice(0, 6);
    const poolStart = purePoolStart;

    // Pick 4 of 6 with smart anti-rep
    const four = pickMexicanoFour(pool, partnerCount, opponentCount, key);

    // Preserve top-to-bottom order in the chosen 4
    const fourSorted = pool.filter((id) => four.includes(id));
    const rest = pool.filter((id) => !four.includes(id));

    // Compute average rank for sorting the courts (lower = stronger = lower court no.)
    const avgRank = fourSorted.reduce((s, id) => s + sortedIds.indexOf(id), 0) / 4;
    blocks.push({ ids: fourSorted, avgRank, kind: 'pure' });

    // If we have a leftover from the previous pool, merge it with the new leftover
    if (restCarry) {
      const restFour = [...restCarry.ids, ...rest];
      // Preserve rank order: the earlier leftover has a lower rank (stronger) than the new
      const restFourSorted = sortedIds.filter((id) => restFour.includes(id));
      // The average rank lies between the two pools
      const restAvgRank = (restCarry.fromPoolStart + 3 + poolStart + 3) / 2;
      blocks.push({ ids: restFourSorted, avgRank: restAvgRank, kind: 'rest' });
      restCarry = null;
    } else {
      // Store the leftover for later — fill it up with the next pool
      restCarry = { ids: rest, fromPoolStart: poolStart };
    }

    remaining.splice(0, 6);
    purePoolStart += 6;
  }

  // Handle the remaining players
  if (remaining.length === 4) {
    // 4 players left
    if (restCarry) {
      // We have 2 leftover + 4 new — use the first 2 of the 4 to fill the rest group,
      // and the last 2 + nothing into a pool? That doesn't work out.
      // Best solution: the 4 new become their own court (bottom), the 2 leftovers remain hanging.
      // This should not really happen with valid player counts, but as a fail-safe:
      // set the 2 leftovers + top 2 of the bottom-4 as a rest court, and the remaining 2 left over.
      // That can't work. Fallback: let the 2 leftovers play with each other + 2 from the top.
      // In practice: switch to classic handling — not a common scenario.
      // We tolerate this by merging into 6 and running the normal pool handling:
      const merged = [...restCarry.ids, ...remaining]; // 2 + 4 = 6
      // Pick 4 of these 6
      const four = pickMexicanoFour(merged, partnerCount, opponentCount, key);
      const fourSorted = sortedIds.filter((id) => four.includes(id));
      const rest2 = merged.filter((id) => !four.includes(id));
      const avgRank = fourSorted.reduce((s, id) => s + sortedIds.indexOf(id), 0) / 4;
      blocks.push({ ids: fourSorted, avgRank, kind: 'pure' });
      // The 2 that weren't chosen are left hanging — but we have no more courts.
      // Should never happen with a valid player count (divisible by 4 + at least 12).
      // Logs a warning for debugging.
      if (rest2.length > 0) {
        console.warn('Pool 6: 2 spillere kunne ikke plasseres på en bane', rest2);
      }
      restCarry = null;
    } else {
      // No rest-carry: 4 players become a pure court (bottom)
      const fourSorted = sortedIds.filter((id) => remaining.includes(id));
      const avgRank = fourSorted.reduce((s, id) => s + sortedIds.indexOf(id), 0) / 4;
      blocks.push({ ids: fourSorted, avgRank, kind: 'bottom' });
    }
    remaining.splice(0, 4);
  } else if (remaining.length === 2 && restCarry) {
    // 2 leftover from the previous pool + 2 new = rest group (but no "new pool" to make)
    const restFour = [...restCarry.ids, ...remaining];
    const restFourSorted = sortedIds.filter((id) => restFour.includes(id));
    const restAvgRank = restCarry.fromPoolStart + 4; // slightly below its pool
    blocks.push({ ids: restFourSorted, avgRank: restAvgRank, kind: 'rest' });
    restCarry = null;
    remaining.splice(0, 2);
  }

  // Sort blocks by avgRank → court 1 = strongest
  blocks.sort((a, b) => a.avgRank - b.avgRank);

  return blocks.map((b, idx) => ({ ids: b.ids, court: idx + 1, kind: b.kind }));
}

// Pool 8 blocks: 8 players per block, 2 courts per block.
// Within the block: smart distribution that minimizes total repetition.
export function generatePool8Plan(sortedIds, history) {
  const { partnerCount, opponentCount, key } = history;
  const blocks = []; // { ids: [4], avgRank, court? }
  let i = 0;

  while (i < sortedIds.length) {
    const remaining = sortedIds.length - i;

    if (remaining >= 8) {
      // Full block of 8: consider all 35 ways to split into 4+4
      const block = sortedIds.slice(i, i + 8);
      const combos = mexicanoCombinations(block, 4);
      let bestScore = Infinity;
      const bestCandidates = [];

      for (const c1 of combos) {
        const c2 = block.filter((id) => !c1.includes(id));
        const score =
          scoreMexicanoCombo(c1, partnerCount, opponentCount, key) +
          scoreMexicanoCombo(c2, partnerCount, opponentCount, key);
        if (score < bestScore) {
          bestScore = score;
          bestCandidates.length = 0;
          bestCandidates.push({ c1, c2 });
        } else if (score === bestScore) {
          bestCandidates.push({ c1, c2 });
        }
      }

      // Random among the tied ones
      const { c1, c2 } = bestCandidates[Math.floor(Math.random() * bestCandidates.length)];

      // The strongest 4 (lowest average rank) get the lower court number
      const r1 = c1.reduce((s, id) => s + sortedIds.indexOf(id), 0) / 4;
      const r2 = c2.reduce((s, id) => s + sortedIds.indexOf(id), 0) / 4;
      const strong = r1 < r2 ? c1 : c2;
      const weak = r1 < r2 ? c2 : c1;

      // Preserve top-to-bottom order
      const strongSorted = sortedIds.filter((id) => strong.includes(id));
      const weakSorted = sortedIds.filter((id) => weak.includes(id));

      blocks.push({ ids: strongSorted, avgRank: Math.min(r1, r2) });
      blocks.push({ ids: weakSorted, avgRank: Math.max(r1, r2) });
      i += 8;
    } else if (remaining === 4) {
      // Only 4 left
      const four = sortedIds.slice(i, i + 4);
      const avgRank = four.reduce((s, id) => s + sortedIds.indexOf(id), 0) / 4;
      blocks.push({ ids: four, avgRank });
      i += 4;
    } else if (remaining === 6) {
      // 6 left — fallback to pool 4 + rest handling, but that is too complicated.
      // Pragmatic: use pickMexicanoFour on 6, then a pure 4-court for the rest? No, that becomes 4+2.
      // The only meaningful option: use pool 4 on the first 4, drop 2 (should never happen).
      const four = sortedIds.slice(i, i + 4);
      const avgRank = four.reduce((s, id) => s + sortedIds.indexOf(id), 0) / 4;
      blocks.push({ ids: four, avgRank });
      i += 4;
      // The rest is ignored — should never happen if numCourts*4 = sortedIds.length
      break;
    } else {
      break;
    }
  }

  blocks.sort((a, b) => a.avgRank - b.avgRank);
  return blocks.map((b, idx) => ({ ids: b.ids, court: idx + 1, kind: 'block8' }));
}

// Pool 4 classic: each court = 4 consecutive players, no choice.
export function generatePool4Plan(sortedIds) {
  const blocks = [];
  for (let i = 0; i < sortedIds.length; i += 4) {
    const four = sortedIds.slice(i, i + 4);
    if (four.length < 4) break;
    blocks.push({ ids: four, court: blocks.length + 1, kind: 'classic' });
  }
  return blocks;
}

// Decide whether a round should be random
export function isMexicanoRandomRound(roundNum, config) {
  if (!roundNum || roundNum < 1) return true;
  const rfr = config?.randomFirstRounds ?? 1;
  if (roundNum <= rfr) return true;
  const ren = config?.randomEveryNthRound ?? 0;
  if (ren > 0 && roundNum % ren === 0) return true;
  return false;
}

// Sort active players by standings (descending points, ELO as tie-breaker)
// Sort active players by the TOURNAMENT's ranking (config.rankingOrder).
//
// IMPORTANT: calcAmericanoStandings already returns a fully sorted list
// where `rank` reflects the user's chosen ranking ("Wins first", custom
// drag-and-drop order, h2h position, etc). We reuse that sorting
// directly instead of re-sorting on hardcoded criteria.
//
// ELO is used ONLY as the final tie-breaker when two players have exactly the same standing
// (all enabled ranking criteria are equal). This typically happens in round 1
// when everyone has 0 points — then ELO provides a deterministic seeding.
export function sortPlayersByStandings(activePlayers, standings) {
  // Build a lookup table: playerId -> index in the standings list (lower = better).
  // Injured players are not in the standings list for sorting, but all active ones are.
  const rankIdx = new Map();
  standings.forEach((s, i) => {
    rankIdx.set(s.playerId, i);
  });

  return [...activePlayers].sort((a, b) => {
    const ia = rankIdx.has(a.id) ? rankIdx.get(a.id) : Number.MAX_SAFE_INTEGER;
    const ib = rankIdx.has(b.id) ? rankIdx.get(b.id) : Number.MAX_SAFE_INTEGER;
    if (ia !== ib) return ia - ib;
    // Final tie-breaker (completely equal in standings): ELO descending.
    // In practice used only in round 1 before anyone has played.
    return (b.elo || 1500) - (a.elo || 1500);
  });
}

// Build a round with Mexicano matchmaking
// activePlayerIds should be divisible by 4. sortedActivePlayers is sorted by standings.
export function generateMexicanoRoundPlan(sortedActivePlayers, history, players, config, isRandom) {
  const { partnerCount, opponentCount: _opponentCount, key } = history;
  const poolSize = config?.poolSize ?? 6;
  const numCourts = Math.floor(sortedActivePlayers.length / 4);

  if (isRandom) {
    // Entirely random: shuffle all players, split 4-by-4
    const shuffled = shuffle(sortedActivePlayers.map((p) => p.id));
    const plan = [];
    for (let i = 0; i < numCourts; i++) {
      const four = shuffled.slice(i * 4, i * 4 + 4);
      // Random pairing within the quartet
      const v = Math.floor(Math.random() * 3);
      if (v === 0) plan.push({ team1: [four[0], four[1]], team2: [four[2], four[3]] });
      else if (v === 1) plan.push({ team1: [four[0], four[2]], team2: [four[1], four[3]] });
      else plan.push({ team1: [four[0], four[3]], team2: [four[1], four[2]] });
    }
    return plan;
  }

  // Standings-based: route to the correct pool algorithm.
  // All algorithms return { ids: [4 sorted by rank], court } blocks.
  const sortedIds = sortedActivePlayers.map((p) => p.id);
  let blocks;
  if (poolSize === 4) {
    blocks = generatePool4Plan(sortedIds);
  } else if (poolSize === 8) {
    blocks = generatePool8Plan(sortedIds, history);
  } else {
    // poolSize 6 (default)
    blocks = generatePool6Plan(sortedIds, history);
  }

  // For each block: pick the pair constellation with smart anti-repetition
  const plan = blocks.map((block) => {
    const pairing = pickMexicanoPairing(block.ids, partnerCount, key);
    return {
      team1: pairing.team1,
      team2: pairing.team2,
      // Metadata for debugging and UI (kind = 'pure' | 'rest' | 'bottom' | 'classic' | 'block8')
      kind: block.kind,
    };
  });

  return plan;
}

// Main entry point: generate a round for a mexicano tournament
// opts.forceRandom = true → override and generate a random round (for the "Random now" button)
export function generateMexicanoRound(mexicano, opts: any = {}) {
  const { players, matches, config, currentRound } = mexicano;

  // Filter out injured players
  const activePlayers = players.filter((p) => !p.injured);
  const playerIds = activePlayers.map((p) => p.id);

  // Build history from regular (non-final) matches
  const regularMatches = matches.filter((m) => !m.isFinal);
  const history = buildAmericanoHistory(regularMatches);

  // Decide who plays this round (divisible by 4)
  const totalCourts = config.courts || 1;
  const maxPlayersThisRound = Math.min(playerIds.length, totalCourts * 4);
  const activeCount = Math.floor(maxPlayersThisRound / 4) * 4;
  const benchCount = playerIds.length - activeCount;

  let benched = [];
  let activeIds = playerIds;

  if (benchCount > 0) {
    benched = selectBenched(playerIds, history.benchCount, benchCount);
    activeIds = playerIds.filter((id) => !benched.includes(id));
  }

  // Build standings for sorting
  const standings = calcAmericanoStandings(mexicano);

  // Sort active players by standings
  const activePlayerObjs = activePlayers.filter((p) => activeIds.includes(p.id));
  const sorted = sortPlayersByStandings(activePlayerObjs, standings);

  // Decide whether the round is random (config-driven or forced via opts)
  const roundNum = currentRound + 1;
  const isRandom = opts.forceRandom === true || isMexicanoRandomRound(roundNum, config);

  // Generate the plan
  const courtsPlan = generateMexicanoRoundPlan(sorted, history, activePlayers, config, isRandom);

  // Build the match objects
  const newMatches = courtsPlan.map((plan, idx) => ({
    id: `mx_r${roundNum}_c${idx + 1}`,
    round: roundNum,
    court: idx + 1,
    team1: { p1Id: plan.team1[0], p2Id: plan.team1[1] },
    team2: { p1Id: plan.team2[0], p2Id: plan.team2[1] },
    score1: null,
    score2: null,
    sets: [],
    completed: false,
    isFinal: false,
    benched,
    // Mexicano metadata: was this a random round?
    isRandomRound: isRandom,
    // Pool type for this court (used for visual labeling in the UI):
    //   'classic'  — pool 4 (always 4 consecutive)
    //   'pure'     — pool 6, pure pool
    //   'rest'     — pool 6, rest group (between two pools)
    //   'bottom'   — pool 6, bottom court (no leftover)
    //   'block8'   — pool 8, block court
    //   undefined  — random round
    poolKind: plan.kind,
  }));

  return newMatches;
}

// Default Mexicano configuration — inherits Americano but has its own defaults
export const MEXICANO_DEFAULT_CONFIG = {
  ...AMERICANO_DEFAULT_CONFIG,
  name: 'Mexicano',
  tournamentType: 'mexicano',

  // ──── Mexicano-specific fields ───────────────────────────────────────
  // poolSize: 4 = classic Mexicano (top 4 always on court 1)
  //           6 = court 1 picks 4 of the top 6 (variation, default for 16+ players)
  //           8 = court 1 picks 4 of the top 8 (max variation, costs level precision)
  poolSize: 6,

  // randomFirstRounds: how many rounds at the start are entirely random.
  // Default 1 (classic Mexicano — the first round is a draw).
  randomFirstRounds: 1,

  // randomEveryNthRound: every Nth round is random (0 = never).
  // Used to break player loops in long tournaments.
  randomEveryNthRound: 0,

  // Mexicano does not support team mode for now
  teamMode: false,
  teams: null,
};

// Create a new Mexicano tournament
export function createMexicanoTournament({ config, players }) {
  if (!Array.isArray(players) || players.length < 4) {
    return { error: 'Minst 4 spillere kreves' };
  }

  // Mexicano does not require a particular count, but rounds will have benching if
  // the count is not divisible by 4 (same as Americano).

  // Validate pool size against the player count
  const validPools = validMexicanoPoolSizes(players.length);
  const requestedPool = config?.poolSize ?? defaultMexicanoPoolSize(players.length);
  const poolSize = validPools.includes(requestedPool) ? requestedPool : validPools[0];

  return {
    id: `mx_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
    type: 'mexicano',
    config: {
      ...MEXICANO_DEFAULT_CONFIG,
      ...config,
      tournamentType: 'mexicano',
      teamMode: false,
      teams: null,
      poolSize,
    },
    players: players.map((p) => ({ ...p, startElo: p.elo })),
    matches: [],
    currentRound: 0,
    standings: {},
    status: 'setup',
    finalMatches: [],
    startedAt: null,
    completedAt: null,
  };
}

// Export the mexicano engine

// ============================================================
// MIXICANO ENGINE — Mix Mexicano with gender mixing
// ============================================================
//
// Mixicano = Mexicano + the requirement that each team has one man and one woman.
//
// Player structure: each player has gender: 'M' or 'F' (shared across the whole
// app). In addition, a player can be temporarily "changed" for the tournament via
// config.genderOverrides (a map from playerId → gender). This is used when
// the gender balance is uneven and the user wants to "borrow" a player for the other gender.
//
// Pool algorithm:
//   Pool 4 = 2M + 2W per court (classic mix)
//   Pool 6 = 3M + 3W per pool, pick 2M + 2W per court, 1M + 1W leftover
//   Pool 8 = 4M + 4W per block, smart distribution 2+2 / 2+2
//
// Pair constellation (within 4 players — 2M + 2W):
//   Mc: M1+W1 vs M2+W2 (classic)
//   Md: M1+W2 vs M2+W1 (variant)
//   (M1+M2 vs W1+W2 is NOT allowed — breaks the mix rule)
//
// Auto mode on uneven balance: if there are more of one gender than the other,
// the "bonus" players are automatically given an inverted role for the round (marked in the UI).
// ============================================================

// Helper: get the effective gender (with overrides)
export function effectiveGender(player, genderOverrides) {
  if (genderOverrides && genderOverrides[player.id]) {
    return genderOverrides[player.id];
  }
  return player.gender || 'M';
}

// Generate all 2-combinations of an array
export function pairCombinations(arr) {
  const result = [];
  for (let i = 0; i < arr.length; i++) {
    for (let j = i + 1; j < arr.length; j++) {
      result.push([arr[i], arr[j]]);
    }
  }
  return result;
}

// Score a mix court (2M + 2W) based on repetition
export function scoreMixFour(maleIds, femaleIds, partnerCount, opponentCount, key) {
  let s = 0;
  // Check all 4 players against each other for repetition
  const all = [...maleIds, ...femaleIds];
  for (let i = 0; i < all.length; i++) {
    for (let j = i + 1; j < all.length; j++) {
      const p = partnerCount.get(key(all[i], all[j])) || 0;
      const o = opponentCount.get(key(all[i], all[j])) || 0;
      s += p * p * 10 + o * o * 1;
    }
  }
  return s;
}

// Pick 2M + 2W from a pool of M candidates and W candidates (smart anti-rep)
export function pickMixFour(maleCandidates, femaleCandidates, partnerCount, opponentCount, key) {
  if (maleCandidates.length < 2 || femaleCandidates.length < 2) return null;

  // If exactly 2+2: no choice
  if (maleCandidates.length === 2 && femaleCandidates.length === 2) {
    return { males: [...maleCandidates], females: [...femaleCandidates] };
  }

  // All possible pairs of men × all possible pairs of women
  const malePairs = pairCombinations(maleCandidates);
  const femalePairs = pairCombinations(femaleCandidates);

  const scored = [];
  for (const mp of malePairs) {
    for (const fp of femalePairs) {
      scored.push({
        males: mp,
        females: fp,
        s: scoreMixFour(mp, fp, partnerCount, opponentCount, key),
      });
    }
  }
  scored.sort((a, b) => a.s - b.s);

  // Random among the tied ones
  const minScore = scored[0].s;
  const best = scored.filter((x) => x.s === minScore);
  return best[Math.floor(Math.random() * best.length)];
}

// Pick the pairing for 2M + 2W — only 2 possible (Mc and Md)
// Mc: M1+W1 vs M2+W2
// Md: M1+W2 vs M2+W1
export function pickMixPairing(males, females, partnerCount, key) {
  const [m1, m2] = males;
  const [k1, k2] = females;

  const Mc = { team1: [m1, k1], team2: [m2, k2], type: 'Mc' };
  const Md = { team1: [m1, k2], team2: [m2, k1], type: 'Md' };

  const mcScore =
    Math.pow(partnerCount.get(key(m1, k1)) || 0, 2) +
    Math.pow(partnerCount.get(key(m2, k2)) || 0, 2);
  const mdScore =
    Math.pow(partnerCount.get(key(m1, k2)) || 0, 2) +
    Math.pow(partnerCount.get(key(m2, k1)) || 0, 2);

  if (mcScore < mdScore) return Mc;
  if (mdScore < mcScore) return Md;
  // Tie: random
  return Math.random() < 0.5 ? Mc : Md;
}

// Pool 4 mix: 2M + 2W per court from each pool
export function generateMixPool4Plan(sortedMales, sortedFemales, _history) {
  const blocks = [];
  let i = 0,
    j = 0;
  while (i + 2 <= sortedMales.length && j + 2 <= sortedFemales.length) {
    const males = sortedMales.slice(i, i + 2);
    const females = sortedFemales.slice(j, j + 2);
    const avgRank = (males[0].rank + males[1].rank + females[0].rank + females[1].rank) / 4;
    blocks.push({
      males: males.map((p) => p.id),
      females: females.map((p) => p.id),
      avgRank,
      kind: 'classic',
    });
    i += 2;
    j += 2;
  }
  return blocks;
}

// Pool 6 mix: pool of 3M + 3W, pick 2M + 2W per court, 1M + 1W leftover
export function generateMixPool6Plan(sortedMales, sortedFemales, history) {
  const { partnerCount, opponentCount, key } = history;
  const blocks = [];
  const remainingM = [...sortedMales];
  const remainingK = [...sortedFemales];
  let restCarry = null; // { males: [1], females: [1], fromPoolRank: number }

  while (remainingM.length >= 3 && remainingK.length >= 3) {
    const poolM = remainingM.slice(0, 3);
    const poolK = remainingK.slice(0, 3);

    // Pick 2M + 2W with smart anti-rep
    const picked = pickMixFour(
      poolM.map((p) => p.id),
      poolK.map((p) => p.id),
      partnerCount,
      opponentCount,
      key,
    );

    // 1 M and 1 W are left over
    const restM = poolM.filter((p) => !picked.males.includes(p.id));
    const restK = poolK.filter((p) => !picked.females.includes(p.id));

    // Average rank for sorting the courts
    const allIds = [...picked.males, ...picked.females];
    const allPlayers = [...poolM, ...poolK].filter((p) => allIds.includes(p.id));
    const avgRank = allPlayers.reduce((s, p) => s + p.rank, 0) / 4;

    blocks.push({
      males: picked.males,
      females: picked.females,
      avgRank,
      kind: 'pure',
    });

    // Handle the leftover
    if (restCarry) {
      // Merge with the carry
      const restFour = pickMixFour(
        [...restCarry.males.map((p) => p.id), ...restM.map((p) => p.id)],
        [...restCarry.females.map((p) => p.id), ...restK.map((p) => p.id)],
        partnerCount,
        opponentCount,
        key,
      );
      const restPlayers = [...restCarry.males, ...restCarry.females, ...restM, ...restK];
      const restAvgRank = restPlayers.reduce((s, p) => s + p.rank, 0) / 4;
      blocks.push({
        males: restFour.males,
        females: restFour.females,
        avgRank: restAvgRank,
        kind: 'rest',
      });
      restCarry = null;
    } else {
      restCarry = { males: restM, females: restK };
    }

    remainingM.splice(0, 3);
    remainingK.splice(0, 3);
  }

  // Handle the rest
  if (remainingM.length >= 2 && remainingK.length >= 2) {
    const males = remainingM.slice(0, 2);
    const females = remainingK.slice(0, 2);
    const players = [...males, ...females];
    const avgRank = players.reduce((s, p) => s + p.rank, 0) / 4;

    if (restCarry) {
      // We have 1+1 leftover + 2+2 new = 3+3, run pickMixFour on the whole thing
      const all = pickMixFour(
        [...restCarry.males.map((p) => p.id), ...males.map((p) => p.id)],
        [...restCarry.females.map((p) => p.id), ...females.map((p) => p.id)],
        partnerCount,
        opponentCount,
        key,
      );
      const allPlayers = [...restCarry.males, ...restCarry.females, ...males, ...females];
      const newAvgRank = allPlayers.reduce((s, p) => s + p.rank, 0) / 4;
      blocks.push({
        males: all.males,
        females: all.females,
        avgRank: newAvgRank,
        kind: 'pure',
      });
      restCarry = null;
    } else {
      blocks.push({
        males: males.map((p) => p.id),
        females: females.map((p) => p.id),
        avgRank,
        kind: 'bottom',
      });
    }
    remainingM.splice(0, 2);
    remainingK.splice(0, 2);
  } else if (remainingM.length === 1 && remainingK.length === 1 && restCarry) {
    // 1 M + 1 W + 1 M + 1 W = 2+2 leftover
    const restMales = [...restCarry.males.map((p) => p.id), remainingM[0].id];
    const restFemales = [...restCarry.females.map((p) => p.id), remainingK[0].id];
    const players = [...restCarry.males, ...restCarry.females, remainingM[0], remainingK[0]];
    const avgRank = players.reduce((s, p) => s + p.rank, 0) / 4;
    blocks.push({
      males: restMales,
      females: restFemales,
      avgRank,
      kind: 'rest',
    });
    restCarry = null;
  }

  blocks.sort((a, b) => a.avgRank - b.avgRank);
  return blocks;
}

// Pool 8 mix: 4M + 4W per block, smart distribution 2M+2W / 2M+2W on 2 courts
export function generateMixPool8Plan(sortedMales, sortedFemales, history) {
  const { partnerCount, opponentCount, key } = history;
  const blocks = [];
  let i = 0,
    j = 0;

  while (i + 4 <= sortedMales.length && j + 4 <= sortedFemales.length) {
    const blockM = sortedMales.slice(i, i + 4).map((p) => p.id);
    const blockK = sortedFemales.slice(j, j + 4).map((p) => p.id);

    // Pick 2 of 4 men (6 combinations) × 2 of 4 women (6 combinations) = 36 possibilities
    // We want to split the block into two courts — one gets 2M+2W, the other gets the remaining 2M+2W
    let bestScore = Infinity;
    const bestCandidates = [];

    const malePairs = pairCombinations(blockM);
    const femalePairs = pairCombinations(blockK);

    for (const mp1 of malePairs) {
      for (const fp1 of femalePairs) {
        const mp2 = blockM.filter((id) => !mp1.includes(id));
        const fp2 = blockK.filter((id) => !fp1.includes(id));
        const score =
          scoreMixFour(mp1, fp1, partnerCount, opponentCount, key) +
          scoreMixFour(mp2, fp2, partnerCount, opponentCount, key);
        if (score < bestScore) {
          bestScore = score;
          bestCandidates.length = 0;
          bestCandidates.push({ mp1, fp1, mp2, fp2 });
        } else if (score === bestScore) {
          bestCandidates.push({ mp1, fp1, mp2, fp2 });
        }
      }
    }

    const { mp1, fp1, mp2, fp2 } =
      bestCandidates[Math.floor(Math.random() * bestCandidates.length)];

    // Court 1 = the strongest (lowest average rank)
    const blockMPlayers = sortedMales.slice(i, i + 4);
    const blockKPlayers = sortedFemales.slice(j, j + 4);
    const avgRank1 =
      [...mp1, ...fp1]
        .map((id) => [...blockMPlayers, ...blockKPlayers].find((p) => p.id === id).rank)
        .reduce((a, b) => a + b, 0) / 4;
    const avgRank2 =
      [...mp2, ...fp2]
        .map((id) => [...blockMPlayers, ...blockKPlayers].find((p) => p.id === id).rank)
        .reduce((a, b) => a + b, 0) / 4;

    if (avgRank1 < avgRank2) {
      blocks.push({ males: mp1, females: fp1, avgRank: avgRank1, kind: 'block8' });
      blocks.push({ males: mp2, females: fp2, avgRank: avgRank2, kind: 'block8' });
    } else {
      blocks.push({ males: mp2, females: fp2, avgRank: avgRank2, kind: 'block8' });
      blocks.push({ males: mp1, females: fp1, avgRank: avgRank1, kind: 'block8' });
    }

    i += 4;
    j += 4;
  }

  // Leftover handling (should not happen if the balance is correct)
  while (i + 2 <= sortedMales.length && j + 2 <= sortedFemales.length) {
    const males = sortedMales.slice(i, i + 2).map((p) => p.id);
    const females = sortedFemales.slice(j, j + 2).map((p) => p.id);
    const players = [...sortedMales.slice(i, i + 2), ...sortedFemales.slice(j, j + 2)];
    const avgRank = players.reduce((s, p) => s + p.rank, 0) / 4;
    blocks.push({ males, females, avgRank, kind: 'classic' });
    i += 2;
    j += 2;
  }

  blocks.sort((a, b) => a.avgRank - b.avgRank);
  return blocks;
}

// Main entry point for a Mixicano round
export function generateMixicanoRound(mixicano, opts: any = {}) {
  const { players, matches, config, currentRound } = mixicano;

  // Filter out injured players
  const activePlayers = players.filter((p) => !p.injured);
  const genderOverrides = config?.genderOverrides || {};

  // Build history
  const regularMatches = matches.filter((m) => !m.isFinal);
  const history = buildAmericanoHistory(regularMatches);

  // Decide how many players play this round
  const totalCourts = config.courts || 1;
  const maxPlayersThisRound = Math.min(activePlayers.length, totalCourts * 4);
  const activeCount = Math.floor(maxPlayersThisRound / 4) * 4;
  const benchCount = activePlayers.length - activeCount;

  let benched = [];
  let activeIds = activePlayers.map((p) => p.id);

  if (benchCount > 0) {
    benched = selectBenched(activeIds, history.benchCount, benchCount);
    activeIds = activePlayers.filter((p) => !benched.includes(p.id)).map((p) => p.id);
  }

  // Decide whether the round is random
  const roundNum = currentRound + 1;
  const isRandom = opts.forceRandom === true || isMexicanoRandomRound(roundNum, config);

  // Build standings and sort active players
  const standings = calcAmericanoStandings(mixicano);
  const activePlayerObjs = activePlayers.filter((p) => activeIds.includes(p.id));
  const sorted = sortPlayersByStandings(activePlayerObjs, standings);

  // Annotate the players with rank and effective gender
  const annotated = sorted.map((p, idx) => ({
    ...p,
    rank: idx,
    effGender: effectiveGender(p, genderOverrides),
  }));

  // Split into men and women (effective gender)
  const sortedMales = annotated.filter((p) => p.effGender === 'M');
  const sortedFemales = annotated.filter((p) => p.effGender === 'F');

  // Check whether the balance allows a mix draw
  const canMix =
    sortedMales.length >= 2 &&
    sortedFemales.length >= 2 &&
    sortedMales.length + sortedFemales.length >= 4 &&
    sortedMales.length === sortedFemales.length;

  let courtsPlan;

  if (isRandom && canMix) {
    // Random mix round: shuffle men and women separately, make 2M+2W per court.
    // Each court gets 2 random men + 2 random women. The pairing within
    // the 4 is also random among Mc/Md (M+M vs W+W is never allowed).
    const shuffledM = shuffle(sortedMales.map((p) => p.id));
    const shuffledF = shuffle(sortedFemales.map((p) => p.id));
    const numCourts = Math.min(Math.floor(shuffledM.length / 2), Math.floor(shuffledF.length / 2));
    const plan = [];
    for (let i = 0; i < numCourts; i++) {
      const m = shuffledM.slice(i * 2, i * 2 + 2);
      const k = shuffledF.slice(i * 2, i * 2 + 2);
      // Random Mc or Md
      const v = Math.floor(Math.random() * 2);
      if (v === 0) {
        // Mc: M1+W1 vs M2+W2
        plan.push({ team1: [m[0], k[0]], team2: [m[1], k[1]], kind: 'random' });
      } else {
        // Md: M1+W2 vs M2+W1
        plan.push({ team1: [m[0], k[1]], team2: [m[1], k[0]], kind: 'random' });
      }
    }
    courtsPlan = plan;
  } else if (isRandom || !canMix) {
    // Random mode WITHOUT mix (uneven balance): ordinary shuffle, gender ignored.
    // Only happens when the gender balance does not allow 2M+2W per court.
    const shuffled = shuffle(annotated.map((p) => p.id));
    const plan = [];
    const numCourts = Math.floor(shuffled.length / 4);
    for (let i = 0; i < numCourts; i++) {
      const four = shuffled.slice(i * 4, i * 4 + 4);
      const v = Math.floor(Math.random() * 3);
      if (v === 0)
        plan.push({
          team1: [four[0], four[1]],
          team2: [four[2], four[3]],
          kind: isRandom ? 'random' : 'imbalanced',
        });
      else if (v === 1)
        plan.push({
          team1: [four[0], four[2]],
          team2: [four[1], four[3]],
          kind: isRandom ? 'random' : 'imbalanced',
        });
      else
        plan.push({
          team1: [four[0], four[3]],
          team2: [four[1], four[2]],
          kind: isRandom ? 'random' : 'imbalanced',
        });
    }
    courtsPlan = plan;
  } else {
    // Mix mode
    const poolSize = config?.poolSize ?? 6;
    let blocks;
    if (poolSize === 4) blocks = generateMixPool4Plan(sortedMales, sortedFemales, history);
    else if (poolSize === 8) blocks = generateMixPool8Plan(sortedMales, sortedFemales, history);
    else blocks = generateMixPool6Plan(sortedMales, sortedFemales, history);

    // For each block: pick the Mc/Md pairing
    courtsPlan = blocks.map((b) => {
      const pairing = pickMixPairing(b.males, b.females, history.partnerCount, history.key);
      return {
        team1: pairing.team1,
        team2: pairing.team2,
        kind: b.kind,
      };
    });
  }

  // Build the match objects
  const newMatches = courtsPlan.map((plan, idx) => ({
    id: `mxi_r${roundNum}_c${idx + 1}`,
    round: roundNum,
    court: idx + 1,
    team1: { p1Id: plan.team1[0], p2Id: plan.team1[1] },
    team2: { p1Id: plan.team2[0], p2Id: plan.team2[1] },
    score1: null,
    score2: null,
    sets: [],
    completed: false,
    isFinal: false,
    benched,
    isRandomRound: isRandom,
    poolKind: plan.kind,
  }));

  return newMatches;
}

// Default Mixicano config
export const MIXICANO_DEFAULT_CONFIG = {
  ...MEXICANO_DEFAULT_CONFIG,
  name: 'Mix Mexicano',
  tournamentType: 'mixicano',

  // genderOverrides: temporary change of a player's gender for this tournament.
  // Format: { playerId: 'M' | 'F' }. No gender change = uses the player's default.
  genderOverrides: {},
};

// Create a Mixicano tournament
export function createMixicanoTournament({ config, players }) {
  if (!Array.isArray(players) || players.length < 4) {
    return { error: 'Minst 4 spillere kreves' };
  }

  const validPools = validMexicanoPoolSizes(players.length);
  const requestedPool = config?.poolSize ?? defaultMexicanoPoolSize(players.length);
  const poolSize = validPools.includes(requestedPool) ? requestedPool : validPools[0];

  return {
    id: `mxi_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
    type: 'mixicano',
    config: {
      ...MIXICANO_DEFAULT_CONFIG,
      ...config,
      tournamentType: 'mixicano',
      teamMode: false,
      teams: null,
      poolSize,
      genderOverrides: config?.genderOverrides || {},
    },
    players: players.map((p) => ({ ...p, startElo: p.elo })),
    matches: [],
    currentRound: 0,
    standings: {},
    status: 'setup',
    finalMatches: [],
    startedAt: null,
    completedAt: null,
  };
}

// Export the mixicano engine

// ============================================================
// TEAM MEXICANO ENGINE — Mexicano with fixed partner pairs
// ============================================================
//
// Team Mexicano = Team Americano + Mexicano matchmaking.
//
// The players are fixed into pairs as in Team Americano (config.teams: [{p1Id, p2Id}]).
// The difference from Team Americano: the next round is generated from the TEAM RANKING,
// so the strongest team meets the strongest team — giving more even matches the further into
// the tournament you get.
//
// The data structure is identical to Team Americano:
//   - type: 'team_mexicano'
//   - config.teamMode: true
//   - config.teams: [{p1Id, p2Id}, ...]
//   - config.poolSize, randomFirstRounds, randomEveryNthRound (same as Mexicano)
//
// Pool size in team-mode:
//   - Pool 4 (default for small tournaments): 2 teams per court (rank 1 vs rank 2 on court 1)
//   - Pool 6: 3 teams per pool, pick 2 of 3, leftover = 1 + 1 from the neighboring pool
//   - Pool 8: 4 teams per block, 2 courts per block, smart distribution
// ============================================================

// Compute team ranking: sum each team's player standings
// Returns a sorted array of { teamKey, p1Id, p2Id, points, wins, losses, pointDiff }
export function calcTeamMexicanoStandings(americano) {
  const { config } = americano;
  const teams = Array.isArray(config?.teams) ? config.teams : [];

  // Get player standings from the Americano standings
  const playerStandings = calcAmericanoStandings(americano);
  const stMap = new Map(playerStandings.map((s) => [s.playerId, s]));

  // For each team: ONE player's record stands for the team.
  const teamStandings = teams.map((t) => {
    const empty = {
      wins: 0,
      losses: 0,
      points: 0,
      pointDiff: 0,
      draws: 0,
      winCredit: 0,
      injured: false,
    };
    const s1 = stMap.get(t.p1Id) || empty;
    const s2 = stMap.get(t.p2Id) || empty;
    // The two records are equal while both play — they are on court together
    // and score together. They part the moment ONE partner is flagged injured:
    // bench compensation is withheld from the injured player and still granted
    // to the fit one. The team is represented by the partner who is still
    // playing (#648, decided 2026-10-09); when both or neither are injured the
    // records agree and p1 stands in, as before.
    const rep = s1.injured && !s2.injured ? s2 : s1;
    return {
      teamKey: teamKey(t.p1Id, t.p2Id),
      p1Id: t.p1Id,
      p2Id: t.p2Id,
      wins: rep.wins,
      losses: rep.losses,
      draws: rep.draws,
      winCredit: rep.winCredit,
      points: rep.points,
      pointDiff: rep.pointDiff,
    };
  });

  // Sort with the same ranking order as calcAmericanoStandings — resolved
  // by the same function, so an empty array or a legacy tieBreaker config
  // cannot rank the teams differently from the players.
  const rankingOrder = resolveAmericanoRankingOrder(config);

  const compareTeams = (a, b) => {
    for (const item of rankingOrder) {
      if (!item.enabled) continue;
      if (item.id === 'wins') {
        // Draws count half (#568), the same credit the player ranking uses.
        if (b.winCredit !== a.winCredit) return b.winCredit - a.winCredit;
      } else if (item.id === 'pointsDiff') {
        if (b.pointDiff !== a.pointDiff) return b.pointDiff - a.pointDiff;
      } else if (item.id === 'points') {
        if (b.points !== a.points) return b.points - a.points;
      }
      // h2h is ignored for team ranking — unclear how it should be computed
    }
    return 0;
  };

  teamStandings.sort(compareTeams);
  return teamStandings;
}

// Pick 2 of 3 teams with smart anti-rep (random among the tied ones)
export function pickTeamPair(threeTeams, teamHistory) {
  const { teamOpponent, oppKey } = teamHistory;
  // 3 possible combinations: (1,2), (1,3), (2,3)
  const combos = [
    [threeTeams[0], threeTeams[1]],
    [threeTeams[0], threeTeams[2]],
    [threeTeams[1], threeTeams[2]],
  ];
  const scored = combos.map((c) => ({
    c,
    s: Math.pow(teamOpponent.get(oppKey(c[0].teamKey, c[1].teamKey)) || 0, 2),
  }));
  scored.sort((a, b) => a.s - b.s);
  const minScore = scored[0].s;
  const best = scored.filter((x) => x.s === minScore);
  return best[Math.floor(Math.random() * best.length)].c;
}

// Pick 4 of 4 teams (2+2 against each other) for pool 8
export function pickTeamFourBlock(fourTeams, teamHistory) {
  const { teamOpponent, oppKey } = teamHistory;
  // 3 ways to split 4 teams into 2+2: (12,34), (13,24), (14,23)
  const splits = [
    [
      [fourTeams[0], fourTeams[1]],
      [fourTeams[2], fourTeams[3]],
    ],
    [
      [fourTeams[0], fourTeams[2]],
      [fourTeams[1], fourTeams[3]],
    ],
    [
      [fourTeams[0], fourTeams[3]],
      [fourTeams[1], fourTeams[2]],
    ],
  ];
  const scored = splits.map((s) => {
    const [m1, m2] = s;
    const score =
      Math.pow(teamOpponent.get(oppKey(m1[0].teamKey, m1[1].teamKey)) || 0, 2) +
      Math.pow(teamOpponent.get(oppKey(m2[0].teamKey, m2[1].teamKey)) || 0, 2);
    return { s, score };
  });
  scored.sort((a, b) => a.score - b.score);
  const minScore = scored[0].score;
  const best = scored.filter((x) => x.score === minScore);
  return best[Math.floor(Math.random() * best.length)].s;
}

// Pool 4 for team: 2 teams per court (rank 1+2, 3+4, 5+6, ...)
export function generateTeamMexicanoPool4Plan(sortedTeams) {
  const blocks = [];
  for (let i = 0; i + 1 < sortedTeams.length; i += 2) {
    const t1 = sortedTeams[i];
    const t2 = sortedTeams[i + 1];
    blocks.push({
      teams: [t1, t2],
      avgRank: i + 0.5, // average of two rank positions
      kind: 'classic',
    });
  }
  return blocks;
}

// Pool 6 for team: 3 teams per pool, pick 2 of 3, 1 team leftover.
// Two neighboring pool leftovers (1 + 1) form a new "rest" court.
export function generateTeamMexicanoPool6Plan(sortedTeams, teamHistory) {
  const blocks = [];
  const remaining = [...sortedTeams];
  let restCarry = null; // a single team from the previous pool
  let _purePoolStart = 0;

  while (remaining.length >= 3) {
    const pool = remaining.slice(0, 3);

    // Pick 2 of 3 teams with smart anti-rep
    const picked = pickTeamPair(pool, teamHistory);

    // 1 team becomes a leftover
    const restTeam = pool.find((t) => !picked.includes(t));

    // Average rank for sorting the courts
    const avgRank = picked.reduce((s, t) => s + sortedTeams.indexOf(t), 0) / 2;
    blocks.push({ teams: picked, avgRank, kind: 'pure' });

    if (restCarry) {
      // Merge with the carry leftover
      const restPair = [restCarry, restTeam];
      const restAvgRank = (sortedTeams.indexOf(restCarry) + sortedTeams.indexOf(restTeam)) / 2;
      blocks.push({ teams: restPair, avgRank: restAvgRank, kind: 'rest' });
      restCarry = null;
    } else {
      restCarry = restTeam;
    }

    remaining.splice(0, 3);
    _purePoolStart += 3;
  }

  // Handle the rest — several possible states depending on the total number of teams.
  if (remaining.length === 2) {
    if (restCarry) {
      // 1 leftover + 2 new = 3 total — pick 2 of 3 with anti-rep
      const merged = [restCarry, ...remaining];
      const picked = pickTeamPair(merged, teamHistory);
      const avgRank = picked.reduce((s, t) => s + sortedTeams.indexOf(t), 0) / 2;
      blocks.push({ teams: picked, avgRank, kind: 'pure' });
      // 1 team left — cannot play alone
      const leftover = merged.find((t) => !picked.includes(t));
      if (leftover) {
        // Mark as "sitting out" — handled by the engine as a benched team
        // (not returned in blocks, but the calling code must handle it)
        // For now: don't try to force everyone in, let this team sit out.
      }
      restCarry = null;
    } else {
      // 2 teams left = one court. COPY remaining (not a ref) before the splice below
      // removes the elements the block references.
      const pair = [...remaining];
      const avgRank = pair.reduce((s, t) => s + sortedTeams.indexOf(t), 0) / 2;
      blocks.push({ teams: pair, avgRank, kind: 'bottom' });
    }
    remaining.splice(0, 2);
  } else if (remaining.length === 1) {
    // 1 team left + possibly restCarry
    if (restCarry) {
      // We have 1 carry + 1 remaining = 2 teams = one court (rest court)
      // E.g. 10 teams pool 6: pool 1 carries t2, pool 2 paired with the carry, pool 3 carries t8,
      // 1 team (t9) left → t8 + t9 = rest court.
      const pair = [restCarry, remaining[0]];
      const avgRank = (sortedTeams.indexOf(restCarry) + sortedTeams.indexOf(remaining[0])) / 2;
      blocks.push({ teams: pair, avgRank, kind: 'rest' });
      restCarry = null;
    }
    // If no carry: 1 team left alone, cannot play — sits out.
    // Happens if the total number of teams is odd and there is no carry right now.
    remaining.splice(0, 1);
  }

  // If there is still a restCarry without a partner: the team sits out this round
  // (not returned in blocks).

  blocks.sort((a, b) => a.avgRank - b.avgRank);
  return blocks;
}

// Pool 8 for team: 4 teams per block, 2 courts per block, smart distribution
export function generateTeamMexicanoPool8Plan(sortedTeams, teamHistory) {
  const blocks = [];
  let i = 0;

  while (i < sortedTeams.length) {
    const remaining = sortedTeams.length - i;

    if (remaining >= 4) {
      // Full block: 4 teams → 2 courts
      const block = sortedTeams.slice(i, i + 4);
      const split = pickTeamFourBlock(block, teamHistory);

      // Stronger court first (lowest average rank)
      const avg1 = (sortedTeams.indexOf(split[0][0]) + sortedTeams.indexOf(split[0][1])) / 2;
      const avg2 = (sortedTeams.indexOf(split[1][0]) + sortedTeams.indexOf(split[1][1])) / 2;

      if (avg1 < avg2) {
        blocks.push({ teams: split[0], avgRank: avg1, kind: 'block8' });
        blocks.push({ teams: split[1], avgRank: avg2, kind: 'block8' });
      } else {
        blocks.push({ teams: split[1], avgRank: avg2, kind: 'block8' });
        blocks.push({ teams: split[0], avgRank: avg1, kind: 'block8' });
      }
      i += 4;
    } else if (remaining === 2) {
      // 2 teams left — one court
      const pair = sortedTeams.slice(i, i + 2);
      const avg = (i + i + 1) / 2;
      blocks.push({ teams: pair, avgRank: avg, kind: 'classic' });
      i += 2;
    } else {
      break;
    }
  }

  blocks.sort((a, b) => a.avgRank - b.avgRank);
  return blocks;
}

// Main entry point for a Team Mexicano round
export function generateTeamMexicanoRound(americano, opts: any = {}) {
  const { players, matches, config, currentRound } = americano;
  const teams = Array.isArray(config?.teams) ? config.teams : [];

  // Filter out teams with injured players
  const injuredSet = new Set(players.filter((p) => p.injured).map((p) => p.id));
  const activeTeams = teams.filter((t) => !injuredSet.has(t.p1Id) && !injuredSet.has(t.p2Id));

  // Build history
  const regularMatches = matches.filter((m) => !m.isFinal);
  const teamHistory = buildTeamHistory(regularMatches, teams);

  // Number of teams that can actually play this round:
  // - must be divisible by 2 (a pair of teams = one match)
  // - must fit the number of courts (one court = 2 teams)
  const totalCourts = config.courts || 1;
  const maxTeamsThisRound = Math.min(activeTeams.length, totalCourts * 2);
  const activeTeamCount = Math.floor(maxTeamsThisRound / 2) * 2;
  const benchTeamCount = activeTeams.length - activeTeamCount;

  let benchedTeams = [];
  let playingTeams = activeTeams;

  if (benchTeamCount > 0) {
    benchedTeams = selectBenchedTeams(activeTeams, teamHistory.teamBenched, benchTeamCount);
    const benchedKeys = new Set(benchedTeams.map((t) => teamKey(t.p1Id, t.p2Id)));
    playingTeams = activeTeams.filter((t) => !benchedKeys.has(teamKey(t.p1Id, t.p2Id)));
  }
  const benchedPlayerIds = benchedTeams.flatMap((t) => [t.p1Id, t.p2Id]);

  // Decide whether it is a random round
  const roundNum = currentRound + 1;
  const isRandom = opts.forceRandom === true || isMexicanoRandomRound(roundNum, config);

  // Build the team ranking for sorting
  const teamStandings = calcTeamMexicanoStandings(americano);
  // Build a list of active team stats sorted by rank
  const playingKeys = new Set(playingTeams.map((t) => teamKey(t.p1Id, t.p2Id)));
  const sortedTeamObjs = teamStandings.filter((ts) => playingKeys.has(ts.teamKey));

  let blocks;

  if (isRandom) {
    // Random round: shuffle teams and make pairs
    const shuffled = shuffle([...sortedTeamObjs]);
    blocks = [];
    for (let k = 0; k + 1 < shuffled.length; k += 2) {
      blocks.push({
        teams: [shuffled[k], shuffled[k + 1]],
        avgRank: k + 0.5,
        kind: 'random',
      });
    }
  } else {
    const poolSize = config?.poolSize ?? 6;
    if (poolSize === 4) blocks = generateTeamMexicanoPool4Plan(sortedTeamObjs);
    else if (poolSize === 8) blocks = generateTeamMexicanoPool8Plan(sortedTeamObjs, teamHistory);
    else blocks = generateTeamMexicanoPool6Plan(sortedTeamObjs, teamHistory);
  }

  // Build the match objects
  const newMatches = blocks.map((b, idx) => ({
    id: `tmx_r${roundNum}_c${idx + 1}`,
    round: roundNum,
    court: idx + 1,
    team1: { p1Id: b.teams[0].p1Id, p2Id: b.teams[0].p2Id },
    team2: { p1Id: b.teams[1].p1Id, p2Id: b.teams[1].p2Id },
    score1: null,
    score2: null,
    sets: [],
    completed: false,
    isFinal: false,
    benched: benchedPlayerIds,
    isRandomRound: isRandom,
    poolKind: b.kind,
  }));

  return newMatches;
}

// Default Team Mexicano config
export const TEAM_MEXICANO_DEFAULT_CONFIG = {
  ...MEXICANO_DEFAULT_CONFIG,
  name: 'Team Mexicano',
  tournamentType: 'team_mexicano',
  teamMode: true,
  teams: [],
  // Default pool 4 (classic) for small team tournaments. Pool 6/8 require
  // 6+ and 8+ teams respectively (= 12+/16+ players).
  poolSize: 4,
};

// Create a Team Mexicano tournament
export function createTeamMexicanoTournament({ config, players }) {
  if (!Array.isArray(players) || players.length < 4) {
    return { error: 'Minst 4 spillere kreves (2 lag)' };
  }
  if (players.length % 2 !== 0) {
    return { error: 'Team Mexicano krever et partall antall spillere' };
  }

  const teams = Array.isArray(config?.teams) ? config.teams : null;
  if (!teams || teams.length !== players.length / 2) {
    return { error: 'Team Mexicano krever et komplett sett med makkerpar' };
  }
  if (teams.length < 2) {
    return { error: 'Team Mexicano krever minst 2 lag' };
  }

  // Validate all teams
  const playerIds = new Set(players.map((p) => p.id));
  const pairedIds = new Set();
  for (const t of teams) {
    if (!t?.p1Id || !t?.p2Id || t.p1Id === t.p2Id) {
      return { error: 'Hvert lag må ha to ulike spillere' };
    }
    if (!playerIds.has(t.p1Id) || !playerIds.has(t.p2Id)) {
      return { error: 'Lag inneholder spillere som ikke er i turneringen' };
    }
    if (pairedIds.has(t.p1Id) || pairedIds.has(t.p2Id)) {
      return { error: 'En spiller kan kun være på ett lag' };
    }
    pairedIds.add(t.p1Id);
    pairedIds.add(t.p2Id);
  }

  // The pool size must fit the number of teams (min teams = pool size / 2 courts)
  // Pool 4 = 2 teams minimum. Pool 6 = 6 teams minimum. Pool 8 = 8 teams minimum.
  const numTeams = teams.length;
  const requestedPool = config?.poolSize ?? 4;
  let poolSize = requestedPool;
  if (poolSize === 8 && numTeams < 8) poolSize = numTeams >= 6 ? 6 : 4;
  else if (poolSize === 6 && numTeams < 6) poolSize = 4;

  return {
    id: `tmx_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
    type: 'team_mexicano',
    config: {
      ...TEAM_MEXICANO_DEFAULT_CONFIG,
      ...config,
      tournamentType: 'team_mexicano',
      teamMode: true,
      teams,
      poolSize,
    },
    players: players.map((p) => ({ ...p, startElo: p.elo })),
    matches: [],
    currentRound: 0,
    standings: {},
    status: 'setup',
    finalMatches: [],
    startedAt: null,
    completedAt: null,
  };
}

// Export the team_mexicano engine

// ============================================================
// MIX AMERICANO ENGINE — Americano with gender mixing
// ============================================================
//
// Mix Americano = Americano + the requirement that each team has one man and one woman.
//
// Unlike Mix Mexicano (which uses pool-based matchmaking on the
// standings), Mix Americano uses the same matchmaking as ordinary Americano:
// optimal anti-rep and ELO balance. The difference is that quartets must have 2M+2W,
// and pairs must be 1M+1W (Mc or Md, not M+M vs W+W).
//
// Player structure:
//   - Each player has gender: 'M' or 'F' (shared app database)
//   - config.genderOverrides: temporary change of a player's gender for
//     the tournament (without affecting the profile). Format: { playerId: 'M' | 'F' }
//
// Imbalance handling:
//   - If activeMales !== activeFemales: the gender balance is impossible
//   - Fall back to an ordinary Americano draw (ignoring gender) to avoid
//     some players sitting out all the time
// ============================================================

// Score a mix quartet (2M + 2W) based on repetition — reuses scoreMixFour
// from the Mixicano engine. Used to build the optimal quartet distribution.

// Build quartets (2M+2W) from two lists of men and women.
// Returns an array of { males: [id, id], females: [id, id] }.
//
// Strategy: shuffle both lists, take 2M+2W per quartet. We do this N times
// and choose the distribution with the lowest total cost (repetition + ELO imbalance).
export function partitionMixAmericanoQuartets(
  males,
  females,
  partnerCount,
  opponentCount,
  key,
  players,
) {
  const numQuartets = Math.min(Math.floor(males.length / 2), Math.floor(females.length / 2));
  if (numQuartets === 0) return [];

  let bestQuartets = null;
  let bestCost = Infinity;

  for (let iter = 0; iter < AMERICANO_SEARCH_ITERATIONS; iter++) {
    const shuffledM = shuffle(males);
    const shuffledF = shuffle(females);
    const quartets = [];
    let totalCost = 0;

    for (let i = 0; i < numQuartets; i++) {
      const m = shuffledM.slice(i * 2, i * 2 + 2);
      const f = shuffledF.slice(i * 2, i * 2 + 2);
      const cost = scoreMixFour(m, f, partnerCount, opponentCount, key);

      // ELO imbalance — pick the pairing that best balances ELO between the two teams.
      // We simultaneously choose which Mc/Md is best (less repetition, smaller ELO diff).
      const pid = (id) => players.find((p) => p.id === id);
      const elo = (id) => pid(id)?.elo || 1500;

      // Mc: M1+W1 vs M2+W2  ;  Md: M1+W2 vs M2+W1
      const mcAvg1 = (elo(m[0]) + elo(f[0])) / 2;
      const mcAvg2 = (elo(m[1]) + elo(f[1])) / 2;
      const mdAvg1 = (elo(m[0]) + elo(f[1])) / 2;
      const mdAvg2 = (elo(m[1]) + elo(f[0])) / 2;
      const mcImbalance = Math.abs(mcAvg1 - mcAvg2);
      const mdImbalance = Math.abs(mdAvg1 - mdAvg2);

      totalCost += cost + Math.min(mcImbalance, mdImbalance) * AMERICANO_IMBALANCE_COST;
      quartets.push({ males: m, females: f });
    }

    if (totalCost < bestCost) {
      bestCost = totalCost;
      bestQuartets = quartets;
      if (totalCost === 0) break;
    }
  }

  return bestQuartets || [];
}

// Main entry point for a Mix Americano round
export function generateMixAmericanoRound(americano) {
  const { players, matches, config, currentRound } = americano;

  // Filter out injured players
  const activePlayers = players.filter((p) => !p.injured);
  const genderOverrides = config?.genderOverrides || {};

  // Build history
  const regularMatches = matches.filter((m) => !m.isFinal);
  const history = buildAmericanoHistory(regularMatches);

  // Annotate with effective gender
  const annotated = activePlayers.map((p) => ({
    ...p,
    effGender: effectiveGender(p, genderOverrides),
  }));

  // Split into men and women
  const allMales = annotated.filter((p) => p.effGender === 'M');
  const allFemales = annotated.filter((p) => p.effGender === 'F');

  // Decide how many players can play this round:
  // - must be divisible by 4 in total
  // - must have equal numbers of M and W for a mix draw
  const totalCourts = config.courts || 1;
  const maxPlayersThisRound = Math.min(activePlayers.length, totalCourts * 4);
  const activeCount = Math.floor(maxPlayersThisRound / 4) * 4;
  const benchCount = activePlayers.length - activeCount;

  let benched = [];
  let activeIds = activePlayers.map((p) => p.id);

  if (benchCount > 0) {
    benched = selectBenched(activeIds, history.benchCount, benchCount);
    activeIds = activePlayers.filter((p) => !benched.includes(p.id)).map((p) => p.id);
  }

  // Check whether we can run a mix draw (equal numbers of M and W among the active)
  const activeMales = allMales.filter((p) => activeIds.includes(p.id));
  const activeFemales = allFemales.filter((p) => activeIds.includes(p.id));
  const canMix =
    activeMales.length >= 1 &&
    activeFemales.length >= 1 &&
    activeMales.length === activeFemales.length &&
    (activeMales.length + activeFemales.length) % 4 === 0;

  const roundNum = currentRound + 1;
  let newMatches;

  if (canMix) {
    // Mix mode: build quartets with 2M+2W
    const quartets = partitionMixAmericanoQuartets(
      activeMales.map((p) => p.id),
      activeFemales.map((p) => p.id),
      history.partnerCount,
      history.opponentCount,
      history.key,
      activePlayers,
    );

    // For each quartet: pick Mc or Md with anti-rep
    newMatches = quartets.map((q, idx) => {
      const pairing = pickMixPairing(q.males, q.females, history.partnerCount, history.key);
      return {
        id: `mam_r${roundNum}_c${idx + 1}`,
        round: roundNum,
        court: idx + 1,
        team1: { p1Id: pairing.team1[0], p2Id: pairing.team1[1] },
        team2: { p1Id: pairing.team2[0], p2Id: pairing.team2[1] },
        score1: null,
        score2: null,
        sets: [],
        completed: false,
        isFinal: false,
        benched,
        poolKind: 'mix',
      };
    });
  } else {
    // Imbalanced: fall back to an ordinary Americano draw (gender ignored).
    // Marked as 'imbalanced' in poolKind for tracking.
    const courtsPlan = generateAmericanoRoundPlan(activeIds, history, activePlayers);
    newMatches = courtsPlan.map((plan, idx) => ({
      id: `mam_r${roundNum}_c${idx + 1}`,
      round: roundNum,
      court: idx + 1,
      team1: { p1Id: plan.team1[0], p2Id: plan.team1[1] },
      team2: { p1Id: plan.team2[0], p2Id: plan.team2[1] },
      score1: null,
      score2: null,
      sets: [],
      completed: false,
      isFinal: false,
      benched,
      poolKind: 'imbalanced',
    }));
  }

  return newMatches;
}

// Default Mix Americano config — inherits Americano but has genderOverrides
export const MIX_AMERICANO_DEFAULT_CONFIG = {
  ...AMERICANO_DEFAULT_CONFIG,
  name: 'Mix Americano',
  tournamentType: 'mix_americano',

  // genderOverrides: temporary change of a player's gender for this tournament.
  // Format: { playerId: 'M' | 'F' }.
  genderOverrides: {},
};

// Create a Mix Americano tournament
export function createMixAmericanoTournament({ config, players }) {
  if (!Array.isArray(players) || players.length < 4) {
    return { error: 'Minst 4 spillere kreves' };
  }

  return {
    id: `mam_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
    type: 'mix_americano',
    config: {
      ...MIX_AMERICANO_DEFAULT_CONFIG,
      ...config,
      tournamentType: 'mix_americano',
      teamMode: false,
      teams: null,
      genderOverrides: config?.genderOverrides || {},
    },
    players: players.map((p) => ({ ...p, startElo: p.elo })),
    matches: [],
    currentRound: 0,
    standings: {},
    status: 'setup',
    finalMatches: [],
    startedAt: null,
    completedAt: null,
  };
}

// Export the mix_americano engine

// ============================================================
// CLUB AMERICANO ENGINE — Americano format with two clubs
// ============================================================
//
// Club Americano = Americano where half the players represent
// club 1 and the rest club 2. Each court is ALWAYS played as 2 from club A
// vs 2 from club B (never a same-club team). The results count towards
// the clubs' combined final total.
//
// Configuration:
//   - config.clubs: [{ id, name, logoData }, { id, name, logoData }]
//     Exactly 2 clubs. id is a unique string (e.g. 'clubA', 'clubB').
//   - config.clubMembership: { playerId: clubId }
//     Each player belongs to exactly one club.
//
// Engine strategy:
//   - For each round: split active players by club
//   - Each court: 2 from club A as team 1, 2 from club B as team 2
//   - Within each club: optimal partner pairing with anti-rep (same logic
//     as the Americano engine).
//   - Bench rotation per club (surplus players from a club sit out
//     if the clubs have an uneven count, or if the number of courts < club size/2).
// ============================================================

// Build partner pairs within a club through iterative optimization.
// Returns { pairs: [[id, id], ...], cost }.
export function buildClubPairs(playerIds, partnerCount, key) {
  if (playerIds.length === 0) return { pairs: [], cost: 0 };
  if (playerIds.length % 2 !== 0) {
    // Should not happen if we have chosen the right number of players
    return { pairs: [], cost: Infinity };
  }

  let bestPairs = null;
  let bestCost = Infinity;

  for (let iter = 0; iter < AMERICANO_SEARCH_ITERATIONS; iter++) {
    const shuffled = shuffle(playerIds);
    const pairs = [];
    let cost = 0;

    for (let j = 0; j < shuffled.length; j += 2) {
      const pair = [shuffled[j], shuffled[j + 1]];
      pairs.push(pair);
      const k = key(pair[0], pair[1]);
      cost += (partnerCount.get(k) || 0) * AMERICANO_PARTNER_COST;
    }

    if (cost < bestCost) {
      bestCost = cost;
      bestPairs = pairs;
      if (cost === 0) break;
    }
  }

  return { pairs: bestPairs || [], cost: bestCost };
}

// Main entry point for a Club Americano round
export function generateClubAmericanoRound(americano) {
  const { players, matches, config, currentRound } = americano;
  const clubs = Array.isArray(config?.clubs) ? config.clubs : [];
  const membership = config?.clubMembership || {};

  if (clubs.length !== 2) {
    throw new Error('Club Americano krever nøyaktig 2 klubber');
  }

  // Active players per club (exclude injured)
  const activeA = players.filter((p) => !p.injured && membership[p.id] === clubs[0].id);
  const activeB = players.filter((p) => !p.injured && membership[p.id] === clubs[1].id);

  // Max courts = the smaller of (number of teams from each club, config.courts)
  const totalCourts = config.courts || 1;
  const maxCourtsByA = Math.floor(activeA.length / 2);
  const maxCourtsByB = Math.floor(activeB.length / 2);
  const numCourts = Math.min(maxCourtsByA, maxCourtsByB, totalCourts);

  const roundNum = currentRound + 1;
  if (numCourts === 0) return [];

  // Build history from played matches (not finals)
  const regularMatches = matches.filter((m) => !m.isFinal);
  const history = buildAmericanoHistory(regularMatches);

  // Pick who plays / sits out per club
  const playersNeededPerClub = numCourts * 2;

  const benchedA = selectBenched(
    activeA.map((p) => p.id),
    history.benchCount,
    activeA.length - playersNeededPerClub,
  );
  const benchedB = selectBenched(
    activeB.map((p) => p.id),
    history.benchCount,
    activeB.length - playersNeededPerClub,
  );
  const allBenched = [...benchedA, ...benchedB];

  const playingAIds = activeA.filter((p) => !benchedA.includes(p.id)).map((p) => p.id);
  const playingBIds = activeB.filter((p) => !benchedB.includes(p.id)).map((p) => p.id);

  // Build optimal partner pairs within each club (anti-rep)
  const resultA = buildClubPairs(playingAIds, history.partnerCount, history.key);
  const resultB = buildClubPairs(playingBIds, history.partnerCount, history.key);

  // Sort pairs by average ELO within each club (strongest first)
  const elo = (id) => players.find((p) => p.id === id)?.elo || 1500;
  const pairsA = resultA.pairs
    .map((pair) => ({
      pair,
      avgElo: (elo(pair[0]) + elo(pair[1])) / 2,
    }))
    .sort((a, b) => b.avgElo - a.avgElo);

  const pairsB = resultB.pairs
    .map((pair) => ({
      pair,
      avgElo: (elo(pair[0]) + elo(pair[1])) / 2,
    }))
    .sort((a, b) => b.avgElo - a.avgElo);

  // Match the strongest A vs the strongest B on court 1, etc.
  // server: alternates every other round, but the initial team is chosen at random at
  // the start of the tournament (stored as config.initialServer).
  // Round 1 = initialServer, round 2 = the opposite, round 3 = initialServer, etc.
  const initialServer = config?.initialServer || 'team1';
  const otherServer = initialServer === 'team1' ? 'team2' : 'team1';
  const server = roundNum % 2 === 1 ? initialServer : otherServer;
  const newMatches = pairsA.map((aEntry, idx) => {
    const bEntry = pairsB[idx];
    return {
      id: `cam_r${roundNum}_c${idx + 1}`,
      round: roundNum,
      court: idx + 1,
      team1: { p1Id: aEntry.pair[0], p2Id: aEntry.pair[1] },
      team2: { p1Id: bEntry.pair[0], p2Id: bEntry.pair[1] },
      score1: null,
      score2: null,
      sets: [],
      completed: false,
      isFinal: false,
      benched: allBenched,
      // Track which club each team belongs to
      team1ClubId: clubs[0].id,
      team2ClubId: clubs[1].id,
      server,
    };
  });

  return newMatches;
}

// Compute the clubs' combined score based on all played matches.
// Includes both regular matches AND playoff finals — the finals
// often decide who wins overall, so they count towards the total.
// Format: {
//   [clubId]: { wins, losses, ties, totalScore, opponentScore, matchesPlayed, finalsPlayed },
//   leader: clubId | 'tie' | null,
// }
export function calcClubStandings(americano) {
  const { matches, config } = americano;
  const clubs = Array.isArray(config?.clubs) ? config.clubs : [];
  const standings = {};

  for (const c of clubs) {
    standings[c.id] = {
      clubId: c.id,
      name: c.name,
      logoData: c.logoData,
      wins: 0,
      losses: 0,
      ties: 0,
      totalScore: 0,
      opponentScore: 0,
      matchesPlayed: 0,
      finalsPlayed: 0, // separate counter for finals (informational)
    };
  }

  for (const m of matches) {
    if (!m.completed) continue;
    // Include BOTH regular matches and finals — the finals count towards
    // the club's total in Club Americano.
    const clubA = m.team1ClubId;
    const clubB = m.team2ClubId;
    if (!standings[clubA] || !standings[clubB]) continue;

    const s1 = m.score1 || 0;
    const s2 = m.score2 || 0;

    standings[clubA].totalScore += s1;
    standings[clubA].opponentScore += s2;
    standings[clubA].matchesPlayed += 1;
    standings[clubB].totalScore += s2;
    standings[clubB].opponentScore += s1;
    standings[clubB].matchesPlayed += 1;

    if (m.isFinal) {
      standings[clubA].finalsPlayed += 1;
      standings[clubB].finalsPlayed += 1;
    }

    if (s1 > s2) {
      standings[clubA].wins += 1;
      standings[clubB].losses += 1;
    } else if (s2 > s1) {
      standings[clubB].wins += 1;
      standings[clubA].losses += 1;
    } else {
      standings[clubA].ties += 1;
      standings[clubB].ties += 1;
    }
  }

  // Who is leading? THE TOURNAMENT'S OWN RANKING ORDER decides (#648, decided
  // 2026-10-09) — the same wins → difference → points sequence the player
  // table uses, with a tied match worth half a win. Total score alone could
  // show the club that won fewer matches as leading, which contradicted the
  // Lederboard beside it. Head to head has no meaning between two clubs and
  // is skipped; level on every ordinal criterion is 'tie'.
  const clubAStanding = standings[clubs[0]?.id];
  const clubBStanding = standings[clubs[1]?.id];
  let leader = null;
  if (clubAStanding && clubBStanding) {
    const metric = (st: any, criterion: string): number => {
      if (criterion === 'wins') return st.wins + 0.5 * st.ties;
      if (criterion === 'pointsDiff') return st.totalScore - st.opponentScore;
      if (criterion === 'points') return st.totalScore;
      return 0;
    };
    leader = 'tie';
    for (const item of resolveAmericanoRankingOrder(config)) {
      if (!item.enabled || item.id === 'h2h') continue;
      const a = metric(clubAStanding, item.id);
      const b = metric(clubBStanding, item.id);
      if (a === b) continue;
      leader = a > b ? clubs[0].id : clubs[1].id;
      break;
    }
  }

  return { standings, leader };
}

// ============================================================
// CLUB AMERICANO — generate finals based on club ranking
// ============================================================
// Unlike ordinary Americano (where the top final is 1+3 vs 2+4 or
// 1+2 vs 3+4 BASED ON THE STANDINGS), Club Americano finals are played as
// club-vs-club match-ups based on each club's internal ranking:
//   - Gold final: Club A's #1+#2 vs Club B's #1+#2
//   - Bronze final: Club A's #3+#4 vs Club B's #3+#4
//   - etc.
// The results count towards the club's total (same as regular matches),
// so the finals can decide the overall club match-up.
export function generateClubAmericanoFinals(americano) {
  const standings = calcAmericanoStandings(americano);
  const clubs = Array.isArray(americano.config?.clubs) ? americano.config.clubs : [];
  const membership = americano.config?.clubMembership || {};

  if (clubs.length !== 2) return [];

  // Sort each club's players by standings rank
  const sortedA = standings
    .filter((s) => !s.injured && membership[s.playerId] === clubs[0].id)
    .sort((a, b) => (a.rank || 999) - (b.rank || 999));
  const sortedB = standings
    .filter((s) => !s.injured && membership[s.playerId] === clubs[1].id)
    .sort((a, b) => (a.rank || 999) - (b.rank || 999));

  // The server alternates per final (same principle as regular matches).
  // We continue seamlessly from the last round based on config.initialServer.
  const lastRound = americano.currentRound || 0;
  const initialServer = americano.config?.initialServer || 'team1';
  const otherServer = initialServer === 'team1' ? 'team2' : 'team1';

  const finalMatches = [];
  // Number of finals = min(players from A, players from B) / 2.
  // Since we require equal counts in Club Americano, these are equal.
  const numFinals = Math.floor(Math.min(sortedA.length, sortedB.length) / 2);

  for (let i = 0; i < numFinals; i++) {
    const offset = i * 2;
    const aP1 = sortedA[offset];
    const aP2 = sortedA[offset + 1];
    const bP1 = sortedB[offset];
    const bP2 = sortedB[offset + 1];

    if (!aP1 || !aP2 || !bP1 || !bP2) continue;

    // The server alternates per final the same way as regular rounds
    const finalRoundNum = lastRound + 1 + i;
    const server = finalRoundNum % 2 === 1 ? initialServer : otherServer;

    finalMatches.push({
      id: `cam_final_${i + 1}`,
      round: 0,
      court: i + 1,
      finalNumber: i + 1,
      team1: { p1Id: aP1.playerId, p2Id: aP2.playerId },
      team2: { p1Id: bP1.playerId, p2Id: bP2.playerId },
      score1: null,
      score2: null,
      sets: [],
      completed: false,
      isFinal: true,
      // Club membership is preserved so the scores count towards the total
      team1ClubId: clubs[0].id,
      team2ClubId: clubs[1].id,
      server,
      preStanding: [aP1.playerId, aP2.playerId, bP1.playerId, bP2.playerId],
    });
  }

  return finalMatches;
}

// Default Club Americano config
export const CLUB_AMERICANO_DEFAULT_CONFIG = {
  ...AMERICANO_DEFAULT_CONFIG,
  name: 'Club Americano',
  tournamentType: 'club_americano',
  clubs: [], // [{ id, name, logoData }, { id, name, logoData }]
  clubMembership: {}, // { playerId: clubId }
};

// Create a Club Americano tournament
export function createClubAmericanoTournament({ config, players }) {
  if (!Array.isArray(players) || players.length < 4) {
    return { error: 'Minst 4 spillere kreves' };
  }

  const clubs = config?.clubs || [];
  if (clubs.length !== 2) {
    return { error: 'To klubber kreves' };
  }

  const membership = config?.clubMembership || {};
  // Check that all players have a club
  for (const p of players) {
    if (!membership[p.id]) {
      return { error: `Spiller ${p.name} mangler klubbtilknytning` };
    }
  }

  // Check balance: each club must have at least 2 players, and the counts must be equal
  // (Club Americano requires equivalent clubs for fair play).
  const aCount = players.filter((p) => membership[p.id] === clubs[0].id).length;
  const bCount = players.filter((p) => membership[p.id] === clubs[1].id).length;
  if (aCount < 2 || bCount < 2) {
    return { error: 'Hver klubb må ha minst 2 spillere' };
  }
  if (aCount !== bCount) {
    return { error: `Klubbene må ha like mange spillere (${aCount} mot ${bCount})` };
  }

  // initialServer: randomly 'team1' (club A) or 'team2' (club B) at creation.
  // After this the serving team alternates every other round throughout the tournament.
  // If initialServer = 'team2': round 1 = team2, round 2 = team1, round 3 = team2 etc.
  const initialServer = Math.random() < 0.5 ? 'team1' : 'team2';

  return {
    id: `cam_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
    type: 'club_americano',
    config: {
      ...CLUB_AMERICANO_DEFAULT_CONFIG,
      ...config,
      tournamentType: 'club_americano',
      teamMode: false,
      teams: null,
      clubs,
      clubMembership: membership,
      initialServer,
    },
    players: players.map((p) => ({ ...p, startElo: p.elo })),
    matches: [],
    currentRound: 0,
    standings: {},
    status: 'setup',
    finalMatches: [],
    startedAt: null,
    completedAt: null,
  };
}

// Export the club_americano engine

// ============================================================
// CLUB TEAM AMERICANO ENGINE — two clubs, fixed teams in each club
// ============================================================
//
// Concept: Like Club Americano, but with fixed partner pairs. Each club fields
// teams (pairs of players), and teams from club A play against teams from club B.
// Round-robin principle: each A team meets all B teams at least once before
// repeats. The clubs' combined score decides the winner.
//
// Configuration:
//   - config.clubs: [{ id, name, logoData }, { id, name, logoData }]
//   - config.teams: [{ p1Id, p2Id }, ...]  all teams (pairs)
//   - config.clubMembership: { playerId: clubId }
//     Each player belongs to a club. ALL players in a team MUST be from
//     the same club (validation).
//   - config.initialServer: 'team1' | 'team2' (random, set at create)
// ============================================================

// Match history for the club-team format.
// Returns { matchupCount: Map<aKey::bKey, count>, teamBenched: Map<key, count> }
export function buildClubTeamHistory(matches, teams) {
  const matchupCount = new Map();
  const teamBenched = new Map();

  // Initialize with 0 for all team pairs
  for (const t of teams) {
    teamBenched.set(teamKey(t.p1Id, t.p2Id), 0);
  }

  for (const m of matches) {
    if (m.isFinal) continue; // Finals don't affect the matching of regular rounds

    // A team played if match.team1 / team2 has the players from that team
    if (m.team1 && m.team2) {
      const aKey = teamKey(m.team1.p1Id, m.team1.p2Id);
      const bKey = teamKey(m.team2.p1Id, m.team2.p2Id);
      const k = aKey + '::' + bKey;
      const kRev = bKey + '::' + aKey;
      // Count both directions as the same match-up
      matchupCount.set(k, (matchupCount.get(k) || 0) + 1);
      matchupCount.set(kRev, (matchupCount.get(kRev) || 0) + 1);
    }

    // Benched: a team sits out if both players are in benched
    if (Array.isArray(m.benched) && m.benched.length > 0) {
      const benchedSet = new Set(m.benched);
      for (const t of teams) {
        if (benchedSet.has(t.p1Id) && benchedSet.has(t.p2Id)) {
          const tk = teamKey(t.p1Id, t.p2Id);
          teamBenched.set(tk, (teamBenched.get(tk) || 0) + 1);
        }
      }
    }
  }

  return { matchupCount, teamBenched };
}

// Generate a round in Club Team Americano: pair A teams with B teams using anti-rep.
export function generateClubTeamAmericanoRound(americano) {
  const { players, matches, config, currentRound } = americano;
  const clubs = Array.isArray(config?.clubs) ? config.clubs : [];
  const teams = Array.isArray(config?.teams) ? config.teams : [];
  const membership = config?.clubMembership || {};

  if (clubs.length !== 2) {
    throw new Error('Club Team Americano krever nøyaktig 2 klubber');
  }

  // Filter out teams with injured players
  const injuredSet = new Set(players.filter((p) => p.injured).map((p) => p.id));
  const activeTeams = teams.filter((t) => !injuredSet.has(t.p1Id) && !injuredSet.has(t.p2Id));

  // Partition teams per club
  const teamsA = activeTeams.filter((t) => membership[t.p1Id] === clubs[0].id);
  const teamsB = activeTeams.filter((t) => membership[t.p1Id] === clubs[1].id);

  // Max courts = the smaller of (number of teams in each club, config.courts)
  const totalCourts = config.courts || 1;
  const numCourts = Math.min(teamsA.length, teamsB.length, totalCourts);

  const roundNum = currentRound + 1;
  if (numCourts === 0) return [];

  // Build history
  const regularMatches = matches.filter((m) => !m.isFinal);
  const history = buildClubTeamHistory(regularMatches, teams);

  // Pick which teams play / sit out per club (separate bench rotation)
  const selectPlayingTeams = (clubTeams, needed) => {
    if (clubTeams.length <= needed) return { playing: clubTeams, benched: [] };
    // Sort teams by benched history (fewest benched first → play)
    const sorted = [...clubTeams].sort((x, y) => {
      const bx = history.teamBenched.get(teamKey(x.p1Id, x.p2Id)) || 0;
      const by = history.teamBenched.get(teamKey(y.p1Id, y.p2Id)) || 0;
      return bx - by;
    });
    return {
      playing: sorted.slice(0, needed),
      benched: sorted.slice(needed),
    };
  };

  const aSelect = selectPlayingTeams(teamsA, numCourts);
  const bSelect = selectPlayingTeams(teamsB, numCourts);
  const benchedPlayerIds = [
    ...aSelect.benched.flatMap((t) => [t.p1Id, t.p2Id]),
    ...bSelect.benched.flatMap((t) => [t.p1Id, t.p2Id]),
  ];

  // Build optimal pair-to-pair matching based on anti-rep (greedy).
  // For each A team in rank order: pick the B team with the lowest matchup count.
  const playingA = [...aSelect.playing];
  const playingB = [...bSelect.playing];

  // We try several permutations and choose the one with the lowest total cost
  let bestPairs = null;
  let bestCost = Infinity;

  for (let iter = 0; iter < AMERICANO_SEARCH_ITERATIONS; iter++) {
    const shuffledA = shuffle(playingA);
    const shuffledB = shuffle(playingB);
    let cost = 0;
    const pairs = [];

    for (let i = 0; i < numCourts; i++) {
      const a = shuffledA[i];
      const b = shuffledB[i];
      const aKey = teamKey(a.p1Id, a.p2Id);
      const bKey = teamKey(b.p1Id, b.p2Id);
      const reps = history.matchupCount.get(aKey + '::' + bKey) || 0;
      cost += reps * AMERICANO_OPPONENT_COST;
      pairs.push({ a, b });
    }

    if (cost < bestCost) {
      bestCost = cost;
      bestPairs = pairs;
      if (cost === 0) break;
    }
  }

  if (!bestPairs) return [];

  // Server rotation — same principle as Club Americano
  const initialServer = config?.initialServer || 'team1';
  const otherServer = initialServer === 'team1' ? 'team2' : 'team1';
  const server = roundNum % 2 === 1 ? initialServer : otherServer;

  // Sort by average ELO so the strongest match-up ends up on court 1
  const elo = (id) => players.find((p) => p.id === id)?.elo || 1500;
  const sortedPairs = bestPairs
    .map((p) => ({
      ...p,
      avgElo: (elo(p.a.p1Id) + elo(p.a.p2Id) + elo(p.b.p1Id) + elo(p.b.p2Id)) / 4,
    }))
    .sort((x, y) => y.avgElo - x.avgElo);

  const newMatches = sortedPairs.map((pair, idx) => ({
    id: `cta_r${roundNum}_c${idx + 1}`,
    round: roundNum,
    court: idx + 1,
    team1: { p1Id: pair.a.p1Id, p2Id: pair.a.p2Id },
    team2: { p1Id: pair.b.p1Id, p2Id: pair.b.p2Id },
    score1: null,
    score2: null,
    sets: [],
    completed: false,
    isFinal: false,
    benched: benchedPlayerIds,
    team1ClubId: clubs[0].id,
    team2ClubId: clubs[1].id,
    server,
  }));

  return newMatches;
}

// Club standings — reuses calcClubStandings (same data structure)

// Generate finals: teams from club A are paired against teams from club B based on
// each club's internal ranking (same principle as Club Americano, just
// at the team level).
export function generateClubTeamAmericanoFinals(americano) {
  const standings = calcAmericanoStandings(americano);
  const clubs = Array.isArray(americano.config?.clubs) ? americano.config.clubs : [];
  const teams = Array.isArray(americano.config?.teams) ? americano.config.teams : [];
  const membership = americano.config?.clubMembership || {};

  if (clubs.length !== 2) return [];

  // Build team ranking: one row per team (both players have the same rank)
  const teamRanks = new Map(); // teamKey → { rank, clubId, team }
  for (const s of standings) {
    if (s.injured) continue;
    const team = teams.find((t) => t.p1Id === s.playerId || t.p2Id === s.playerId);
    if (!team) continue;
    const tk = teamKey(team.p1Id, team.p2Id);
    if (teamRanks.has(tk)) continue;
    teamRanks.set(tk, {
      rank: s.rank,
      clubId: membership[team.p1Id],
      team,
    });
  }

  // Split into club A and club B, sort by rank
  const allTeamsRanked = [...teamRanks.values()].sort((x, y) => (x.rank || 999) - (y.rank || 999));
  const aTeams = allTeamsRanked.filter((t) => t.clubId === clubs[0].id);
  const bTeams = allTeamsRanked.filter((t) => t.clubId === clubs[1].id);

  // The server continues the rotation from the last round
  const lastRound = americano.currentRound || 0;
  const initialServer = americano.config?.initialServer || 'team1';
  const otherServer = initialServer === 'team1' ? 'team2' : 'team1';

  const finalMatches = [];
  const numFinals = Math.min(aTeams.length, bTeams.length);

  for (let i = 0; i < numFinals; i++) {
    const aT = aTeams[i].team;
    const bT = bTeams[i].team;
    const finalRoundNum = lastRound + 1 + i;
    const server = finalRoundNum % 2 === 1 ? initialServer : otherServer;

    finalMatches.push({
      id: `cta_final_${i + 1}`,
      round: 0,
      court: i + 1,
      finalNumber: i + 1,
      team1: { p1Id: aT.p1Id, p2Id: aT.p2Id },
      team2: { p1Id: bT.p1Id, p2Id: bT.p2Id },
      score1: null,
      score2: null,
      sets: [],
      completed: false,
      isFinal: true,
      team1ClubId: clubs[0].id,
      team2ClubId: clubs[1].id,
      server,
    });
  }

  return finalMatches;
}

// Default config
export const CLUB_TEAM_AMERICANO_DEFAULT_CONFIG = {
  ...AMERICANO_DEFAULT_CONFIG,
  name: 'Club Team Americano',
  tournamentType: 'club_team_americano',
  teamMode: true,
  teams: [],
  clubs: [],
  clubMembership: {},
  // Team formats start with tennis sets (same as Team Americano)
  pointsType: 'games_only',
  endMode: 'first_set',
  pointsPerGame: 6,
};

// Create a Club Team Americano tournament
export function createClubTeamAmericanoTournament({ config, players }) {
  if (!Array.isArray(players) || players.length < 4) {
    return { error: 'Minst 4 spillere kreves (2 lag i hver klubb)' };
  }

  const clubs = config?.clubs || [];
  if (clubs.length !== 2) {
    return { error: 'To klubber kreves' };
  }

  const teams = config?.teams || [];
  const membership = config?.clubMembership || {};

  // Validate that all team players are from the same club
  for (const t of teams) {
    const c1 = membership[t.p1Id];
    const c2 = membership[t.p2Id];
    if (!c1 || !c2) {
      return { error: 'Alle spillere må være tildelt en klubb' };
    }
    if (c1 !== c2) {
      const p1 = players.find((p) => p.id === t.p1Id);
      const p2 = players.find((p) => p.id === t.p2Id);
      return { error: `Lag (${p1?.name}, ${p2?.name}) har spillere fra ulike klubber` };
    }
  }

  // Count teams per club
  const teamsA = teams.filter((t) => membership[t.p1Id] === clubs[0].id);
  const teamsB = teams.filter((t) => membership[t.p1Id] === clubs[1].id);

  if (teamsA.length < 1 || teamsB.length < 1) {
    return { error: 'Hver klubb må ha minst ett lag' };
  }
  if (teamsA.length !== teamsB.length) {
    return { error: `Klubbene må ha like mange lag (${teamsA.length} vs ${teamsB.length})` };
  }

  const initialServer = Math.random() < 0.5 ? 'team1' : 'team2';

  return {
    id: `cta_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
    type: 'club_team_americano',
    config: {
      ...CLUB_TEAM_AMERICANO_DEFAULT_CONFIG,
      ...config,
      tournamentType: 'club_team_americano',
      teamMode: true,
      teams,
      clubs,
      clubMembership: membership,
      initialServer,
    },
    players: players.map((p) => ({ ...p, startElo: p.elo })),
    matches: [],
    currentRound: 0,
    standings: {},
    status: 'setup',
    finalMatches: [],
    startedAt: null,
    completedAt: null,
  };
}

// ============================================================
// CLUB MEXICANO ENGINE — two clubs, standings-based matching
// ============================================================
//
// Concept: Like Club Americano (club A vs club B on each court), BUT with
// Mexicano rules for who plays against whom:
//   - The best from club A meets the best from club B (court 1)
//   - The second best from club A meets the second best from club B (court 2)
//   - etc. down the list
//
// Pool concept: poolSize regulates how large the rank window is for
// pairing variation within the club.
//   - Pool 4 (strict): fixed pairing — A[N*2]+A[N*2+1] vs B[N*2]+B[N*2+1]
//   - Pool 6: within a pool of 3 from each club, the pairing can be rotated
//   - Pool 8: within a pool of 4 from each club, more pairing variation
//
// Club membership is ALWAYS respected: never same-club partners as teams
// against each other, only club A's teams vs club B's teams.
// ============================================================

// Helper: make pair blocks within a sorted club list based on poolSize.
// Returns blocks of 2 players that form teams for each club.
// history governs pairing variation: players who have been partners many
// times are separated.
export function buildClubMexicanoPairs(sortedPlayerIds, poolSize, history, partnerCountKey) {
  const pairs = [];
  let cursor = 0;

  while (cursor < sortedPlayerIds.length) {
    const remaining = sortedPlayerIds.length - cursor;

    if (remaining < 2) break;

    // Pool block: 2 players (pool 4), 3 (pool 6) or 4 (pool 8) from this club
    const blockSize =
      poolSize === 4 ? 2 : poolSize === 6 ? Math.min(3, remaining) : Math.min(4, remaining);

    const blockIds = sortedPlayerIds.slice(cursor, cursor + blockSize);

    if (blockSize === 2) {
      // Fixed pair, no variation
      pairs.push({ pair: [blockIds[0], blockIds[1]], avgRank: cursor + 0.5 });
      cursor += 2;
    } else if (blockSize === 3) {
      // 3 players → 1 pair + 1 to the next block
      // Pick the pair with the fewest partner-history
      const candidates = [
        [blockIds[0], blockIds[1], blockIds[2]],
        [blockIds[0], blockIds[2], blockIds[1]],
        [blockIds[1], blockIds[2], blockIds[0]],
      ];
      let best = candidates[0];
      let bestReps = Infinity;
      for (const c of candidates) {
        const key = partnerCountKey(c[0], c[1]);
        const reps = history.partnerCount.get(key) || 0;
        if (reps < bestReps) {
          bestReps = reps;
          best = c;
          if (reps === 0) break;
        }
      }
      pairs.push({ pair: [best[0], best[1]], avgRank: cursor + 0.5 });
      // The third player goes back into the list as "waiting"
      sortedPlayerIds.splice(cursor + 2, 1);
      sortedPlayerIds.splice(cursor + 2, 0, best[2]);
      cursor += 2;
    } else if (blockSize === 4) {
      // 4 players → 2 pairs. Consider the 3 possible pairings and pick the one with the lowest history.
      const variants = [
        { p1: [blockIds[0], blockIds[1]], p2: [blockIds[2], blockIds[3]] },
        { p1: [blockIds[0], blockIds[2]], p2: [blockIds[1], blockIds[3]] },
        { p1: [blockIds[0], blockIds[3]], p2: [blockIds[1], blockIds[2]] },
      ];
      let best = variants[0];
      let bestCost = Infinity;
      for (const v of variants) {
        const r1 = history.partnerCount.get(partnerCountKey(v.p1[0], v.p1[1])) || 0;
        const r2 = history.partnerCount.get(partnerCountKey(v.p2[0], v.p2[1])) || 0;
        const cost = r1 + r2;
        if (cost < bestCost) {
          bestCost = cost;
          best = v;
          if (cost === 0) break;
        }
      }
      pairs.push({ pair: [best.p1[0], best.p1[1]], avgRank: cursor + 0.5 });
      pairs.push({ pair: [best.p2[0], best.p2[1]], avgRank: cursor + 2.5 });
      cursor += 4;
    }
  }

  return pairs;
}

export function generateClubMexicanoRound(americano) {
  const { players, matches, config, currentRound } = americano;
  const clubs = Array.isArray(config?.clubs) ? config.clubs : [];
  const membership = config?.clubMembership || {};

  if (clubs.length !== 2) {
    throw new Error('Club Mexicano krever nøyaktig 2 klubber');
  }

  const injuredSet = new Set(players.filter((p) => p.injured).map((p) => p.id));
  const activePlayers = players.filter((p) => !injuredSet.has(p.id));

  const roundNum = currentRound + 1;

  // Build history for anti-rep
  const regularMatches = matches.filter((m) => !m.isFinal);
  const history = buildAmericanoHistory(regularMatches, activePlayers);
  const partnerKeyFn = (a, b) => [a, b].sort().join(':');

  // Sorting: the first round based on ELO, then on standings (rank)
  const isFirstRound = currentRound === 0;
  let sortedA, sortedB;

  if (isFirstRound) {
    sortedA = activePlayers
      .filter((p) => membership[p.id] === clubs[0].id)
      .sort((a, b) => (b.elo || 1500) - (a.elo || 1500))
      .map((p) => p.id);
    sortedB = activePlayers
      .filter((p) => membership[p.id] === clubs[1].id)
      .sort((a, b) => (b.elo || 1500) - (a.elo || 1500))
      .map((p) => p.id);
  } else {
    const standings = calcAmericanoStandings(americano);
    const rankMap = new Map(standings.map((s) => [s.playerId, s.rank]));
    sortedA = activePlayers
      .filter((p) => membership[p.id] === clubs[0].id)
      .sort((a, b) => (rankMap.get(a.id) || 999) - (rankMap.get(b.id) || 999))
      .map((p) => p.id);
    sortedB = activePlayers
      .filter((p) => membership[p.id] === clubs[1].id)
      .sort((a, b) => (rankMap.get(a.id) || 999) - (rankMap.get(b.id) || 999))
      .map((p) => p.id);
  }

  // Bench rotation: if uneven counts from each club, take those with the fewest
  // benched rounds so far. Requires equal counts from each club (validated at create).
  const totalCourts = config.courts || 1;
  const numCourts = Math.min(
    Math.floor(sortedA.length / 2),
    Math.floor(sortedB.length / 2),
    totalCourts,
  );

  // Benched history per player
  const benchedCount = new Map();
  for (const p of activePlayers) benchedCount.set(p.id, 0);
  for (const m of regularMatches) {
    if (Array.isArray(m.benched)) {
      for (const id of m.benched) {
        benchedCount.set(id, (benchedCount.get(id) || 0) + 1);
      }
    }
  }

  const selectPlayingFromClub = (sortedClubIds, needed) => {
    if (sortedClubIds.length <= needed) return { playing: sortedClubIds, benched: [] };
    // Keep the rank sorting, but prioritize those who have been benched a lot
    const sortedByBench = [...sortedClubIds].sort((a, b) => {
      const bA = benchedCount.get(a) || 0;
      const bB = benchedCount.get(b) || 0;
      return bA - bB; // fewest benched first → play
    });
    // Playing = the "needed" with the fewest benched rounds, then sort back by rank
    const playingSet = new Set(sortedByBench.slice(0, needed));
    const playing = sortedClubIds.filter((id) => playingSet.has(id));
    const benched = sortedClubIds.filter((id) => !playingSet.has(id));
    return { playing, benched };
  };

  const aSelect = selectPlayingFromClub(sortedA, numCourts * 2);
  const bSelect = selectPlayingFromClub(sortedB, numCourts * 2);
  const allBenched = [...aSelect.benched, ...bSelect.benched];

  // Build pairs within each club based on pool size
  const poolSize = config?.poolSize ?? 4;
  const pairsA = buildClubMexicanoPairs([...aSelect.playing], poolSize, history, partnerKeyFn);
  const pairsB = buildClubMexicanoPairs([...bSelect.playing], poolSize, history, partnerKeyFn);

  // Match pairs from A with pairs from B based on rank (best vs best)
  const initialServer = config?.initialServer || 'team1';
  const otherServer = initialServer === 'team1' ? 'team2' : 'team1';
  const server = roundNum % 2 === 1 ? initialServer : otherServer;

  const newMatches = [];
  const numActualBanes = Math.min(pairsA.length, pairsB.length);

  for (let i = 0; i < numActualBanes; i++) {
    const aEntry = pairsA[i];
    const bEntry = pairsB[i];

    newMatches.push({
      id: `cmx_r${roundNum}_c${i + 1}`,
      round: roundNum,
      court: i + 1,
      team1: { p1Id: aEntry.pair[0], p2Id: aEntry.pair[1] },
      team2: { p1Id: bEntry.pair[0], p2Id: bEntry.pair[1] },
      score1: null,
      score2: null,
      sets: [],
      completed: false,
      isFinal: false,
      benched: allBenched,
      team1ClubId: clubs[0].id,
      team2ClubId: clubs[1].id,
      server,
    });
  }

  return newMatches;
}

// Finals: reuses generateClubAmericanoFinals (same principle — club-vs-
// club based on the internal club ranking)
export function generateClubMexicanoFinals(americano) {
  // Identical implementation to Club Americano finals
  return generateClubAmericanoFinals(americano);
}

// Default config
export const CLUB_MEXICANO_DEFAULT_CONFIG = {
  ...AMERICANO_DEFAULT_CONFIG,
  name: 'Club Mexicano',
  tournamentType: 'club_mexicano',
  clubs: [],
  clubMembership: {},
  // Mexicano-specific fields
  poolSize: 4,
  initialRandomRounds: 1, // the first round based on ELO (not standings)
};

export function createClubMexicanoTournament({ config, players }) {
  if (!Array.isArray(players) || players.length < 4) {
    return { error: 'Minst 4 spillere kreves' };
  }

  const clubs = config?.clubs || [];
  if (clubs.length !== 2) return { error: 'To klubber kreves' };

  const membership = config?.clubMembership || {};
  for (const p of players) {
    if (!membership[p.id]) return { error: `Spiller ${p.name} mangler klubbtilknytning` };
  }

  const aCount = players.filter((p) => membership[p.id] === clubs[0].id).length;
  const bCount = players.filter((p) => membership[p.id] === clubs[1].id).length;
  if (aCount < 2 || bCount < 2) return { error: 'Hver klubb må ha minst 2 spillere' };
  if (aCount !== bCount)
    return { error: `Klubbene må ha like mange spillere (${aCount} mot ${bCount})` };

  const initialServer = Math.random() < 0.5 ? 'team1' : 'team2';

  return {
    id: `cmx_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
    type: 'club_mexicano',
    config: {
      ...CLUB_MEXICANO_DEFAULT_CONFIG,
      ...config,
      tournamentType: 'club_mexicano',
      teamMode: false,
      teams: null,
      clubs,
      clubMembership: membership,
      initialServer,
    },
    players: players.map((p) => ({ ...p, startElo: p.elo })),
    matches: [],
    currentRound: 0,
    standings: {},
    status: 'setup',
    finalMatches: [],
    startedAt: null,
    completedAt: null,
  };
}
